import React, { useState, useEffect, useRef } from "react";
import {
  Brain,
  Layers,
  Zap,
  TrendingUp,
  Target,
  AlertCircle,
  Trash2,
  Filter,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import "./NeuralNetworkExplorer.css";

const NeuralNetworkExplorer = () => {
  const [currentConcept, setCurrentConcept] = useState(0);
  const [animationStep, setAnimationStep] = useState(0);
  const [weights, setWeights] = useState({
    layer1: [0.8, 0.6, -0.4, 0.7, -0.5, 0.9],
    layer2: [0.7, -0.5, 0.8, -0.3, 0.6, 0.2],
    layer3: [0.9, -0.7, 0.4],
  });
  const [noiseLevel, setNoiseLevel] = useState(0);
  const [userDropped, setUserDropped] = useState([]);
  const [ablationAcc, setAblationAcc] = useState(100);
  const [deepMode, setDeepMode] = useState(true);
  const [trainingMode, setTrainingMode] = useState("pretrain");
  const [selectedCategory, setSelectedCategory] = useState("medical");
  const [hiddenLayerStep, setHiddenLayerStep] = useState(0);
  const [backpropProgress, setBackpropProgress] = useState(0);
  const [deepAnimationProgress, setDeepAnimationProgress] = useState(0);
  const [outputProgress, setOutputProgress] = useState(0);

  const animationRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 700, height: 500 });

  // Responsive dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = Math.min(900, containerRef.current.offsetWidth - 40);
        setDimensions({
          width,
          height: Math.max(420, Math.round(width * 0.55)),
        });
      }
    };
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const concepts = [
    {
      id: "hidden",
      title: "Neuron and Neural Network",
      icon: Brain,
      description:
        "Neurons are tiny workers. Each circle is a neuron. Lines show connections. Together they make decisions! Middle layers process patterns: Raw data → Simple Patterns → Complex Features → Final decision.",
    },
    {
      id: "input",
      title: "Input Signal - The Starting Point",
      icon: Zap,
      description:
        "Data enters at the left (input layer) and flows right. Watch the signals move through the network!",
    },
    {
      id: "transformation",
      title: "Signal Transformation - Weights Matter!",
      icon: TrendingUp,
      description:
        "Every connection has a weight (importance). Green = positive, red = negative. Adjust weights to see how signals transform.",
    },
    {
      id: "noise",
      title: "Signal vs Noise - Clean vs Messy",
      icon: AlertCircle,
      description:
        "Clean data flows smoothly. Noisy data confuses the network. Adjust noise to see accuracy change.",
    },
    {
      id: "dropout",
      title: "Dropout - Smart Training Trick",
      icon: Trash2,
      description:
        "During training we randomly turn off neurons. Pick neurons to disable and see how the network adapts.",
    },
    {
      id: "ablation",
      title: "Ablation - Find Important Neurons",
      icon: Filter,
      description:
        "We systematically remove neurons and check accuracy. See how different neurons affect performance.",
    },
    {
      id: "backprop",
      title: "Backpropagation - Learning from Mistakes",
      icon: Filter,
      description:
        "Forward pass makes prediction, backward pass updates weights. Watch the learning process in action.",
    },
    {
      id: "deep",
      title: "Deep vs Shallow Networks",
      icon: Layers,
      description:
        "Switch between shallow (2 hidden layers) and deep (5 hidden layers) networks.",
    },
    {
      id: "finetune",
      title: "Fine-Tuning - Specialise a Network",
      icon: TrendingUp,
      description:
        "1) Pre-Train: general internet data. 2) Fine-Tune: adapt to specific domains like medical or manufacturing.",
    },
  ];
  const concept = concepts[currentConcept];

  // small reusable visuals
  const Neuron = ({
    x,
    y,
    active = false,
    disabled = false,
    size = 32,
    label = "",
    highlight = false,
  }) => (
    <g>
      <circle
        cx={x}
        cy={y}
        r={size / 2}
        fill={
          disabled
            ? "#cbd5e1"
            : active
            ? "#3b82f6"
            : highlight
            ? "#f59e0b"
            : "#e0e7ff"
        }
        stroke={
          disabled
            ? "#94a3b8"
            : active
            ? "#2563eb"
            : highlight
            ? "#d97706"
            : "#3b82f6"
        }
        strokeWidth="2.5"
        opacity={disabled ? 0.35 : 1}
      />
      {active && !disabled && (
        <circle
          cx={x}
          cy={y}
          r={size / 2 + 8}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="2"
          opacity="0.35"
          className="nne-pulse"
        />
      )}
      {label && (
        <text
          x={x}
          y={y + size / 2 + 14}
          textAnchor="middle"
          fill="#475569"
          fontSize="10"
          fontWeight="600"
        >
          {label}
        </text>
      )}
    </g>
  );

  const SignalDot = ({ x, y, progress = 0, color = "#3b82f6" }) => (
    <g>
      <circle cx={x} cy={y} r="5" fill={color} className="nne-dot-animate" />
      <circle
        cx={x}
        cy={y}
        r="8"
        fill="none"
        stroke={color}
        strokeWidth="1"
        opacity={0.5 - progress * 0.4}
        className="nne-dot-glow"
      />
    </g>
  );

  const Connection = ({
    x1,
    y1,
    x2,
    y2,
    weight = 0.5,
    highlight = false,
    noise = false,
  }) => {
    const thickness = Math.max(1, Math.abs(weight) * 3);
    const color = weight > 0 ? "#10b981" : "#ef4444";
    const opacity = noise
      ? 0.25 + Math.random() * 0.25
      : highlight
      ? 0.9
      : 0.35 + Math.abs(weight) * 0.35;
    return (
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={color}
        strokeWidth={thickness}
        opacity={opacity}
      />
    );
  };

  // helpers to produce consistent layout
  const SPACING_X = 180; // <-- user requested spacing (Option B)
  const inputX = 100;
  const hidden1X = inputX + SPACING_X;
  const hidden2X = inputX + SPACING_X * 2;
  const outputX = inputX + SPACING_X * 3;
  const BASE_Y = 120;
  const LAYER_GAP = 80;

  // returns array of centered y positions for a layer with `count` neurons, given global maxCount
  const layerYs = (count, maxCount) => {
    // center layer vertically around BASE_Y with gaps LAYER_GAP
    const totalHeight = (maxCount - 1) * LAYER_GAP;
    const layerHeight = (count - 1) * LAYER_GAP;
    const offset = Math.round((totalHeight - layerHeight) / 2);
    return Array.from(
      { length: count },
      (_, i) => BASE_Y + offset + i * LAYER_GAP
    );
  };

  // helper to compute controls (UI) y so foreignObject sits below the network
  const controlsYFor = (maxCount, extra = 100) => {
    return BASE_Y + (maxCount - 1) * LAYER_GAP + extra;
  };

  // animations: keep existing hooks when needed
  useEffect(() => {
    if (concept.id === "input") {
      const start = Date.now();
      const dur = 3000;
      const loop = () => {
        setAnimationStep(((Date.now() - start) % dur) / dur);
        animationRef.current = requestAnimationFrame(loop);
      };
      animationRef.current = requestAnimationFrame(loop);
      return () => cancelAnimationFrame(animationRef.current);
    }
  }, [concept.id]);

  useEffect(() => {
    if (concept.id === "output") {
      const start = Date.now();
      const dur = 4000;
      const loop = () => {
        setOutputProgress(((Date.now() - start) % dur) / dur);
        animationRef.current = requestAnimationFrame(loop);
      };
      animationRef.current = requestAnimationFrame(loop);
      return () => cancelAnimationFrame(animationRef.current);
    }
  }, [concept.id]);

  useEffect(() => {
    if (concept.id === "backprop") {
      const start = Date.now();
      const dur = 6000;
      const loop = () => {
        setBackpropProgress(((Date.now() - start) % dur) / dur);
        animationRef.current = requestAnimationFrame(loop);
      };
      animationRef.current = requestAnimationFrame(loop);
      return () => cancelAnimationFrame(animationRef.current);
    }
  }, [concept.id]);

  useEffect(() => {
    if (concept.id === "deep") {
      const start = Date.now();
      const dur = 5000;
      const loop = () => {
        setDeepAnimationProgress(((Date.now() - start) % dur) / dur);
        animationRef.current = requestAnimationFrame(loop);
      };
      animationRef.current = requestAnimationFrame(loop);
      return () => cancelAnimationFrame(animationRef.current);
    }
  }, [concept.id]);

  // RENDER: unify structure so all visualizations compute positions consistently
  const renderVisualization = () => {
    const { width, height } = dimensions;

    // small utility to render connections between arrays of positions
    const renderConnectionsBetween = (
      xs,
      ysFrom,
      xsTo,
      ysTo,
      weightFn = () => 0.5,
      opts = {}
    ) => {
      const lines = [];
      for (let i = 0; i < ysFrom.length; i++) {
        for (let j = 0; j < ysTo.length; j++) {
          lines.push(
            <Connection
              key={`conn-${xs}-${i}-${j}-${xsTo}`}
              x1={xs + 15}
              y1={ysFrom[i]}
              x2={xsTo - 15}
              y2={ysTo[j]}
              weight={weightFn(i, j)}
              noise={opts.noise}
              highlight={opts.highlight && opts.highlight(i, j)}
            />
          );
        }
      }
      return lines;
    };

    if (concept.id === "hidden") {
      const layerCounts = [2, 4, 3, 2];
      const maxCount = Math.max(...layerCounts);
      const inYs = layerYs(layerCounts[0], maxCount);
      const h1Ys = layerYs(layerCounts[1], maxCount);
      const h2Ys = layerYs(layerCounts[2], maxCount);
      const outYs = layerYs(layerCounts[3], maxCount);
      const currentDesc = [
        {
          title: "Input Layer",
          desc: "Raw data enters the network. Each neuron represents one feature.",
          color: "#3b82f6",
        },
        {
          title: "Hidden Layer 1",
          desc: "Detects simple patterns and basic features from the input data.",
          color: "#8b5cf6",
        },
        {
          title: "Hidden Layer 2",
          desc: "Combines simple patterns into more complex features and relationships.",
          color: "#a855f7",
        },
        {
          title: "Output Layer",
          desc: "Produces final prediction based on all processed features.",
          color: "#10b981",
        },
      ][hiddenLayerStep];
      const controlsY = controlsYFor(maxCount);

      return (
        <svg
          width={width}
          height={Math.max(height, controlsY + 220)}
          viewBox={`0 0 ${width} ${Math.max(height, controlsY + 220)}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: "visible" }}
        >
          <text
            x={width / 2}
            y={40}
            textAnchor="middle"
            fill="#8b5cf6"
            fontSize="16"
            fontWeight="600"
          >
            Hidden Layers Processing Pipeline
          </text>

          {/* Layer labels */}
          <text
            x={inputX}
            y={inYs[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Input
          </text>
          <text
            x={hidden1X}
            y={h1Ys[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Hidden 1
          </text>
          <text
            x={hidden2X}
            y={h2Ys[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Hidden 2
          </text>
          <text
            x={outputX}
            y={outYs[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Output
          </text>

          {inYs.map((y, i) => (
            <Neuron
              key={`in-${i}`}
              x={inputX}
              y={y}
              highlight={hiddenLayerStep === 0}
            />
          ))}
          {h1Ys.map((y, i) => (
            <Neuron
              key={`h1-${i}`}
              x={hidden1X}
              y={y}
              highlight={hiddenLayerStep === 1}
            />
          ))}
          {h2Ys.map((y, i) => (
            <Neuron
              key={`h2-${i}`}
              x={hidden2X}
              y={y}
              highlight={hiddenLayerStep === 2}
            />
          ))}
          {outYs.map((y, i) => (
            <Neuron
              key={`out-${i}`}
              x={outputX}
              y={y}
              highlight={hiddenLayerStep === 3}
            />
          ))}

          {renderConnectionsBetween(inputX, inYs, hidden1X, h1Ys)}
          {renderConnectionsBetween(hidden1X, h1Ys, hidden2X, h2Ys)}
          {renderConnectionsBetween(hidden2X, h2Ys, outputX, outYs)}

          <foreignObject x={30} y={controlsY} width={width - 60} height={220}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                padding: "20px",
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              {/* Progress indicator */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                {[0, 1, 2, 3].map((step) => (
                  <div
                    key={step}
                    style={{
                      width: hiddenLayerStep === step ? "32px" : "12px",
                      height: "8px",
                      borderRadius: "4px",
                      background:
                        hiddenLayerStep >= step ? "#8b5cf6" : "#e2e8f0",
                      transition: "all 0.3s ease",
                    }}
                  />
                ))}
              </div>

              {/* Layer information card */}
              <div
                style={{
                  background: `linear-gradient(135deg, ${currentDesc.color}15, ${currentDesc.color}08)`,
                  border: `2px solid ${currentDesc.color}40`,
                  borderRadius: "12px",
                  padding: "20px 24px",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
                  minHeight: "100px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "18px",
                    fontWeight: "700",
                    color: currentDesc.color,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {currentDesc.title}
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#475569",
                    lineHeight: "1.6",
                  }}
                >
                  {currentDesc.desc}
                </p>
              </div>

              {/* Navigation buttons */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <button
                  onClick={() => setHiddenLayerStep((p) => Math.max(0, p - 1))}
                  disabled={hiddenLayerStep === 0}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "10px 20px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: hiddenLayerStep === 0 ? "#cbd5e1" : "#475569",
                    background: hiddenLayerStep === 0 ? "#f1f5f9" : "white",
                    border: "2px solid",
                    borderColor: hiddenLayerStep === 0 ? "#e2e8f0" : "#8b5cf6",
                    borderRadius: "8px",
                    cursor: hiddenLayerStep === 0 ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                    boxShadow:
                      hiddenLayerStep === 0
                        ? "none"
                        : "0 2px 4px rgba(139, 92, 246, 0.1)",
                    opacity: hiddenLayerStep === 0 ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (hiddenLayerStep !== 0) {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 8px rgba(139, 92, 246, 0.2)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (hiddenLayerStep !== 0) {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 4px rgba(139, 92, 246, 0.1)";
                    }
                  }}
                >
                  <ChevronLeft size={18} />
                  Previous
                </button>

                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#64748b",
                    padding: "8px 16px",
                    background: "#f8fafc",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  Step {hiddenLayerStep + 1} of 4
                </div>

                <button
                  onClick={() => setHiddenLayerStep((p) => Math.min(3, p + 1))}
                  disabled={hiddenLayerStep === 3}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "10px 20px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: hiddenLayerStep === 3 ? "#cbd5e1" : "white",
                    background:
                      hiddenLayerStep === 3
                        ? "#f1f5f9"
                        : "linear-gradient(135deg, #8b5cf6, #a855f7)",
                    border: "2px solid",
                    borderColor: hiddenLayerStep === 3 ? "#e2e8f0" : "#8b5cf6",
                    borderRadius: "8px",
                    cursor: hiddenLayerStep === 3 ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                    boxShadow:
                      hiddenLayerStep === 3
                        ? "none"
                        : "0 2px 4px rgba(139, 92, 246, 0.2)",
                    opacity: hiddenLayerStep === 3 ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (hiddenLayerStep !== 3) {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(139, 92, 246, 0.3)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (hiddenLayerStep !== 3) {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 4px rgba(139, 92, 246, 0.2)";
                    }
                  }}
                >
                  Next
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </foreignObject>
        </svg>
      );
    }

    if (concept.id === "input") {
      const layerCounts = [3, 2, 2, 3];
      const maxCount = Math.max(...layerCounts);
      const inYs = layerYs(layerCounts[0], maxCount);
      const h1Ys = layerYs(layerCounts[1], maxCount);
      const h2Ys = layerYs(layerCounts[2], maxCount);
      const outYs = layerYs(layerCounts[3], maxCount);
      const controlsY = controlsYFor(maxCount);

      // Progress from 0 to 1 for full cycle
      const progress = animationStep;

      // Network boundaries with padding
      const networkPadding = 60;
      const networkLeft = inputX - networkPadding;
      const networkRight = outputX + networkPadding;
      const networkTop = Math.min(...inYs) - 40;
      const networkBottom = Math.max(...outYs) + 40;

      // Input emoji position (outside, left of network)
      const emojiX = networkLeft - 80;
      const emojiY = (networkTop + networkBottom) / 2;

      // Output text position (outside, right of network)
      const predictionX = networkRight + 80;
      const predictionY = (networkTop + networkBottom) / 2;

      // Calculate all signal paths through the network
      const allPaths = [];

      // Input to Hidden1 (3 signals from emoji to 3 input neurons to 2 hidden neurons)
      inYs.forEach((inY, inIdx) => {
        h1Ys.forEach((h1Y, h1Idx) => {
          allPaths.push({
            from: [inputX, inY],
            to: [hidden1X, h1Y],
            startTime: 0.05 + inIdx * 0.02,
            duration: 0.15,
            layer: "input-h1",
          });
        });
      });

      // Hidden1 to Hidden2
      h1Ys.forEach((h1Y, h1Idx) => {
        h2Ys.forEach((h2Y, h2Idx) => {
          allPaths.push({
            from: [hidden1X, h1Y],
            to: [hidden2X, h2Y],
            startTime: 0.25 + h1Idx * 0.02,
            duration: 0.15,
            layer: "h1-h2",
          });
        });
      });

      // Hidden2 to Output
      h2Ys.forEach((h2Y, h2Idx) => {
        outYs.forEach((outY, outIdx) => {
          allPaths.push({
            from: [hidden2X, h2Y],
            to: [outputX, outY],
            startTime: 0.45 + h2Idx * 0.02,
            duration: 0.15,
            layer: "h2-output",
          });
        });
      });

      // Calculate which neurons are active based on signal arrival
      const inputActive = progress > 0.05;
      const h1Active = progress > 0.25;
      const h2Active = progress > 0.45;
      const outputActive = progress > 0.65;
      const predictionReady = progress > 0.75;

      return (
        <svg
          width={width}
          height={Math.max(height, controlsY + 180)}
          viewBox={`0 0 ${width} ${Math.max(height, controlsY + 180)}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: "visible" }}
        >
          <text
            x={width / 2}
            y={40}
            textAnchor="middle"
            fill="#3b82f6"
            fontSize="16"
            fontWeight="600"
          >
            Neural Network Signal Processing
          </text>

          {/* Network border */}
          <rect
            x={networkLeft}
            y={networkTop}
            width={networkRight - networkLeft}
            height={networkBottom - networkTop}
            fill="none"
            stroke="#cbd5e1"
            strokeWidth="2"
            strokeDasharray="5,5"
            rx="8"
          />

          {/* Input emoji (outside network) */}
          <text
            x={emojiX}
            y={emojiY}
            textAnchor="middle"
            fontSize="48"
            opacity={progress < 0.05 ? 1 : 0.5}
          >
            🍎
          </text>
          <text
            x={emojiX}
            y={emojiY + 35}
            textAnchor="middle"
            fontSize="10"
            fill="#64748b"
          >
            Input
          </text>

          {/* Signal from emoji to input layer */}
          {progress > 0 && progress < 0.1 && (
            <circle
              cx={emojiX + (inputX - emojiX) * (progress / 0.1)}
              cy={emojiY + (inYs[1] - emojiY) * (progress / 0.1)}
              r="4"
              fill="#3b82f6"
              opacity="0.8"
            >
              <animate
                attributeName="r"
                values="4;6;4"
                dur="0.5s"
                repeatCount="indefinite"
              />
            </circle>
          )}

          {/* Network layers */}
          {inYs.map((y, i) => (
            <Neuron
              key={`in-${i}`}
              x={inputX}
              y={y}
              active={inputActive && progress > 0.05 + i * 0.02}
            />
          ))}
          {h1Ys.map((y, i) => (
            <Neuron
              key={`h1-${i}`}
              x={hidden1X}
              y={y}
              active={h1Active && progress > 0.25 + i * 0.02}
            />
          ))}
          {h2Ys.map((y, i) => (
            <Neuron
              key={`h2-${i}`}
              x={hidden2X}
              y={y}
              active={h2Active && progress > 0.45 + i * 0.02}
            />
          ))}
          {outYs.map((y, i) => (
            <Neuron
              key={`out-${i}`}
              x={outputX}
              y={y}
              active={outputActive && progress > 0.65 + i * 0.02}
            />
          ))}

          {/* Connections */}
          {renderConnectionsBetween(inputX, inYs, hidden1X, h1Ys)}
          {renderConnectionsBetween(hidden1X, h1Ys, hidden2X, h2Ys)}
          {renderConnectionsBetween(hidden2X, h2Ys, outputX, outYs)}

          {/* Signal dots traveling through network */}
          {allPaths.map((path, idx) => {
            const pathProgress = Math.max(
              0,
              Math.min(1, (progress - path.startTime) / path.duration)
            );
            if (pathProgress > 0 && pathProgress < 1) {
              const x =
                path.from[0] + (path.to[0] - path.from[0]) * pathProgress;
              const y =
                path.from[1] + (path.to[1] - path.from[1]) * pathProgress;
              return (
                <circle
                  key={`signal-${idx}`}
                  cx={x}
                  cy={y}
                  r="3"
                  fill="#3b82f6"
                  opacity={0.6 + pathProgress * 0.4}
                />
              );
            }
            return null;
          })}

          {/* Signal from output layer to prediction */}
          {progress > 0.7 && progress < 0.8 && (
            <circle
              cx={outputX + (predictionX - outputX) * ((progress - 0.7) / 0.1)}
              cy={
                outYs[1] + (predictionY - outYs[1]) * ((progress - 0.7) / 0.1)
              }
              r="4"
              fill="#10b981"
              opacity="0.8"
            >
              <animate
                attributeName="r"
                values="4;6;4"
                dur="0.5s"
                repeatCount="indefinite"
              />
            </circle>
          )}

          {/* Output prediction (outside network) */}
          <text
            x={predictionX}
            y={predictionY}
            textAnchor="middle"
            fontSize="32"
            fontWeight="600"
            fill="#10b981"
            opacity={predictionReady ? 1 : 0.3}
          >
            Apple
          </text>
          <text
            x={predictionX}
            y={predictionY + 30}
            textAnchor="middle"
            fontSize="10"
            fill="#64748b"
          >
            Prediction
          </text>

          {/* Progress indicator */}
          <text
            x={width / 2}
            y={networkBottom + 50}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
          >
            Processing: {Math.round(progress * 100)}%
          </text>
        </svg>
      );
    }

    if (concept.id === "transformation") {
      const layerCounts = [2, 3, 3, 2];
      const maxCount = Math.max(...layerCounts);
      const inYs = layerYs(layerCounts[0], maxCount);
      const h1Ys = layerYs(layerCounts[1], maxCount);
      const h2Ys = layerYs(layerCounts[2], maxCount);
      const outYs = layerYs(layerCounts[3], maxCount);
      const controlsY = controlsYFor(maxCount);

      // Initialize weights with default values if not set
      const ensureWeights = (layer, defaults) => {
        return layer.map((w, i) => (w !== undefined ? w : defaults[i]));
      };

      const layer1Weights = ensureWeights(
        weights.layer1 || [],
        [0.6, 0.5, 0.4]
      );
      const layer2Weights = ensureWeights(
        weights.layer2 || [],
        [0.7, 0.3, 0.5]
      );
      const layer3Weights = ensureWeights(
        weights.layer3 || [],
        [0.8, 0.4, 0.6]
      );

      // Define distinct weights for each layer connection
      const weightSliders = [
        {
          label: "Input → Hidden 1 (Connection A)",
          value: layer1Weights[0],
          onChange: (val) =>
            setWeights({
              ...weights,
              layer1: [val, layer1Weights[1], layer1Weights[2]],
            }),
          connectionIndices: { from: 0, to: 0 },
        },
        {
          label: "Input → Hidden 1 (Connection B)",
          value: layer1Weights[1],
          onChange: (val) =>
            setWeights({
              ...weights,
              layer1: [layer1Weights[0], val, layer1Weights[2]],
            }),
          connectionIndices: { from: 1, to: 2 },
        },
        {
          label: "Hidden 1 → Hidden 2",
          value: layer2Weights[0],
          onChange: (val) =>
            setWeights({
              ...weights,
              layer2: [val, layer2Weights[1], layer2Weights[2]],
            }),
          connectionIndices: { from: 1, to: 1 },
        },
        {
          label: "Hidden 2 → Output",
          value: layer3Weights[0],
          onChange: (val) =>
            setWeights({
              ...weights,
              layer3: [val, layer3Weights[1], layer3Weights[2]],
            }),
          connectionIndices: { from: 0, to: 0 },
        },
      ];

      return (
        <svg
          width={width}
          height={Math.max(height, controlsY + 300)}
          viewBox={`0 0 ${width} ${Math.max(height, controlsY + 300)}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: "visible" }}
        >
          <text
            x={width / 2}
            y={40}
            textAnchor="middle"
            fill="#8b5cf6"
            fontSize="16"
            fontWeight="600"
          >
            Weight Transformation & Adjustment
          </text>

          <text
            x={width / 2}
            y={60}
            textAnchor="middle"
            fill="#64748b"
            fontSize="12"
          >
            Adjust weights to see how connection strength affects signal flow
          </text>

          {/* Layer labels */}
          <text
            x={inputX}
            y={inYs[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Input
          </text>
          <text
            x={hidden1X}
            y={h1Ys[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Hidden 1
          </text>
          <text
            x={hidden2X}
            y={h2Ys[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Hidden 2
          </text>
          <text
            x={outputX}
            y={outYs[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Output
          </text>

          {/* Neurons */}
          {inYs.map((y, i) => (
            <Neuron key={`in-${i}`} x={inputX} y={y} active={true} />
          ))}
          {h1Ys.map((y, i) => (
            <Neuron key={`h1-${i}`} x={hidden1X} y={y} active={true} />
          ))}
          {h2Ys.map((y, i) => (
            <Neuron key={`h2-${i}`} x={hidden2X} y={y} active={true} />
          ))}
          {outYs.map((y, i) => (
            <Neuron key={`out-${i}`} x={outputX} y={y} active={true} />
          ))}

          {/* Connections with weight-based rendering */}
          {renderConnectionsBetween(
            inputX,
            inYs,
            hidden1X,
            h1Ys,
            (i, j) => {
              if (i === 0 && j === 0) return weightSliders[0].value;
              if (i === 1 && j === 2) return weightSliders[1].value;
              return 0.3;
            },
            {
              highlight: (i, j) => (i === 0 && j === 0) || (i === 1 && j === 2),
            }
          )}
          {renderConnectionsBetween(
            hidden1X,
            h1Ys,
            hidden2X,
            h2Ys,
            (i, j) => (i === 1 && j === 1 ? weightSliders[2].value : 0.4),
            { highlight: (i, j) => i === 1 && j === 1 }
          )}
          {renderConnectionsBetween(
            hidden2X,
            h2Ys,
            outputX,
            outYs,
            (i, j) => (i === 0 && j === 0 ? weightSliders[3].value : 0.5),
            { highlight: (i, j) => i === 0 && j === 0 }
          )}

          {/* Weight value labels on highlighted connections */}
          <text
            x={(inputX + hidden1X) / 2}
            y={(inYs[0] + h1Ys[0]) / 2 - 10}
            textAnchor="middle"
            fontSize="10"
            fontWeight="600"
            fill={weightSliders[0].value >= 0 ? "#10b981" : "#ef4444"}
          >
            {weightSliders[0].value.toFixed(2)}
          </text>
          <text
            x={(inputX + hidden1X) / 2}
            y={(inYs[1] + h1Ys[2]) / 2 - 10}
            textAnchor="middle"
            fontSize="10"
            fontWeight="600"
            fill={weightSliders[1].value >= 0 ? "#10b981" : "#ef4444"}
          >
            {weightSliders[1].value.toFixed(2)}
          </text>
          <text
            x={(hidden1X + hidden2X) / 2}
            y={(h1Ys[1] + h2Ys[1]) / 2 - 10}
            textAnchor="middle"
            fontSize="10"
            fontWeight="600"
            fill={weightSliders[2].value >= 0 ? "#10b981" : "#ef4444"}
          >
            {weightSliders[2].value.toFixed(2)}
          </text>
          <text
            x={(hidden2X + outputX) / 2}
            y={(h2Ys[0] + outYs[0]) / 2 - 10}
            textAnchor="middle"
            fontSize="10"
            fontWeight="600"
            fill={weightSliders[3].value >= 0 ? "#10b981" : "#ef4444"}
          >
            {weightSliders[3].value.toFixed(2)}
          </text>

          {/* Improved slider controls - 2 per row */}
          <foreignObject x={0} y={controlsY} width={width} height={300}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                padding: "20px",
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              {/* Row 1 - First two sliders */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                {weightSliders.slice(0, 2).map((slider, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: "linear-gradient(to right, #f8fafc, #f1f5f9)",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      padding: "12px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <label
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#475569",
                        }}
                      >
                        {slider.label}
                      </label>
                      <span
                        style={{
                          fontSize: "15px",
                          fontWeight: "700",
                          color: slider.value >= 0 ? "#10b981" : "#ef4444",
                          minWidth: "45px",
                          textAlign: "right",
                        }}
                      >
                        {slider.value.toFixed(2)}
                      </span>
                    </div>

                    <input
                      type="range"
                      min="-1"
                      max="1"
                      step="0.05"
                      value={slider.value}
                      onChange={(e) =>
                        slider.onChange(parseFloat(e.target.value))
                      }
                      style={{
                        width: "100%",
                        height: "6px",
                        borderRadius: "3px",
                        background: `linear-gradient(to right, 
                      #ef4444 0%, 
                      #fbbf24 50%, 
                      #10b981 100%)`,
                        outline: "none",
                        cursor: "pointer",
                        WebkitAppearance: "none",
                        appearance: "none",
                      }}
                    />

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "4px",
                        fontSize: "9px",
                        color: "#94a3b8",
                      }}
                    >
                      <span>-1.0</span>
                      <span>0</span>
                      <span>+1.0</span>
                    </div>

                    <div
                      style={{
                        marginTop: "6px",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        fontWeight: "500",
                        textAlign: "center",
                        background: slider.value >= 0 ? "#d1fae5" : "#fee2e2",
                        color: slider.value >= 0 ? "#065f46" : "#991b1b",
                      }}
                    >
                      {slider.value > 0.5
                        ? "⚡ Strong +"
                        : slider.value > 0
                        ? "✓ Positive"
                        : slider.value === 0
                        ? "○ Neutral"
                        : slider.value > -0.5
                        ? "✗ Negative"
                        : "⚠ Strong -"}
                    </div>
                  </div>
                ))}
              </div>

              {/* Row 2 - Last two sliders */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                {weightSliders.slice(2, 4).map((slider, idx) => (
                  <div
                    key={idx + 2}
                    style={{
                      background: "linear-gradient(to right, #f8fafc, #f1f5f9)",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      padding: "12px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <label
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#475569",
                        }}
                      >
                        {slider.label}
                      </label>
                      <span
                        style={{
                          fontSize: "15px",
                          fontWeight: "700",
                          color: slider.value >= 0 ? "#10b981" : "#ef4444",
                          minWidth: "45px",
                          textAlign: "right",
                        }}
                      >
                        {slider.value.toFixed(2)}
                      </span>
                    </div>

                    <input
                      type="range"
                      min="-1"
                      max="1"
                      step="0.05"
                      value={slider.value}
                      onChange={(e) =>
                        slider.onChange(parseFloat(e.target.value))
                      }
                      style={{
                        width: "100%",
                        height: "6px",
                        borderRadius: "3px",
                        background: `linear-gradient(to right, 
                      #ef4444 0%, 
                      #fbbf24 50%, 
                      #10b981 100%)`,
                        outline: "none",
                        cursor: "pointer",
                        WebkitAppearance: "none",
                        appearance: "none",
                      }}
                    />

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "4px",
                        fontSize: "9px",
                        color: "#94a3b8",
                      }}
                    >
                      <span>-1.0</span>
                      <span>0</span>
                      <span>+1.0</span>
                    </div>

                    <div
                      style={{
                        marginTop: "6px",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        fontWeight: "500",
                        textAlign: "center",
                        background: slider.value >= 0 ? "#d1fae5" : "#fee2e2",
                        color: slider.value >= 0 ? "#065f46" : "#991b1b",
                      }}
                    >
                      {slider.value > 0.5
                        ? "⚡ Strong +"
                        : slider.value > 0
                        ? "✓ Positive"
                        : slider.value === 0
                        ? "○ Neutral"
                        : slider.value > -0.5
                        ? "✗ Negative"
                        : "⚠ Strong -"}
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  marginTop: "4px",
                  padding: "10px",
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: "6px",
                  fontSize: "11px",
                  color: "#1e40af",
                  lineHeight: "1.4",
                }}
              >
                <strong>💡 How weights work:</strong> Positive weights
                strengthen signals, negative weights inhibit them. Larger
                absolute values = stronger effect.
              </div>
            </div>
          </foreignObject>

          {/* CSS for slider thumb styling */}
          <defs>
            <style>{`
          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: white;
            border: 3px solid #8b5cf6;
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
          input[type="range"]::-moz-range-thumb {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: white;
            border: 3px solid #8b5cf6;
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
        `}</style>
          </defs>
        </svg>
      );
    }

    if (concept.id === "noise") {
      const layerCounts = [2, 3, 4, 3];
      const maxCount = Math.max(...layerCounts);
      const inYs = layerYs(layerCounts[0], maxCount);
      const h1Ys = layerYs(layerCounts[1], maxCount);
      const h2Ys = layerYs(layerCounts[2], maxCount);
      const outYs = layerYs(layerCounts[3], maxCount);
      const controlsY = controlsYFor(maxCount);

      // Accuracy decreases with noise
      const baseAccuracy = 95;
      const accuracy = Math.max(30, baseAccuracy - noiseLevel * 65);

      // Signal-to-Noise Ratio (SNR) - higher is better
      const snr = ((1 - noiseLevel) * 100).toFixed(1);

      const statusColor =
        noiseLevel > 0.6 ? "#ef4444" : noiseLevel > 0.3 ? "#f59e0b" : "#10b981";
      const statusBg =
        noiseLevel > 0.6 ? "#fee2e2" : noiseLevel > 0.3 ? "#fef3c7" : "#d1fae5";

      // Input signal representation (clean image)
      const inputEmoji = "🍎";

      // Output prediction based on noise level
      const getOutputPrediction = () => {
        if (noiseLevel === 0)
          return { emoji: "🍎", label: "Apple", confidence: 95 };
        if (noiseLevel < 0.3)
          return { emoji: "🍎", label: "Apple", confidence: 85 };
        if (noiseLevel < 0.6)
          return { emoji: "🍏", label: "Apple?", confidence: 60 };
        return { emoji: "❓", label: "Unknown", confidence: 30 };
      };

      const output = getOutputPrediction();

      // Generate random noise particles based on noise level
      const noiseParticles = [];
      const particleCount = Math.floor(noiseLevel * 30);
      for (let i = 0; i < particleCount; i++) {
        const x = inputX + Math.random() * (outputX - inputX);
        const y = BASE_Y + Math.random() * (maxCount * LAYER_GAP);
        noiseParticles.push({ x, y, id: i });
      }

      return (
        <svg
          width={width}
          height={Math.max(height, controlsY + 320)}
          viewBox={`0 0 ${width} ${Math.max(height, controlsY + 320)}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: "visible" }}
        >
          <text
            x={width / 2}
            y={40}
            textAnchor="middle"
            fill="#8b5cf6"
            fontSize="16"
            fontWeight="600"
          >
            Signal vs Noise: Impact on Neural Network Performance
          </text>

          <text
            x={width / 2}
            y={60}
            textAnchor="middle"
            fill="#64748b"
            fontSize="12"
          >
            Adjust noise level to see how data quality affects predictions
          </text>

          {/* Input signal (outside network, left) */}
          <g>
            <text
              x={inputX - 60}
              y={(Math.min(...inYs) + Math.max(...inYs)) / 2}
              textAnchor="middle"
              fontSize="40"
              opacity={1 - noiseLevel * 0.5}
            >
              {inputEmoji}
            </text>
            <text
              x={inputX - 60}
              y={(Math.min(...inYs) + Math.max(...inYs)) / 2 + 35}
              textAnchor="middle"
              fontSize="10"
              fill="#64748b"
              fontWeight="600"
            >
              Input Signal
            </text>
            {/* Noise overlay on input */}
            {noiseLevel > 0 && (
              <rect
                x={inputX - 85}
                y={(Math.min(...inYs) + Math.max(...inYs)) / 2 - 25}
                width="50"
                height="50"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2"
                strokeDasharray="3,3"
                opacity={noiseLevel}
              />
            )}
          </g>

          {/* Output prediction (outside network, right) */}
          <g>
            <text
              x={outputX + 60}
              y={(Math.min(...outYs) + Math.max(...outYs)) / 2}
              textAnchor="middle"
              fontSize="40"
              opacity={1 - noiseLevel * 0.3}
            >
              {output.emoji}
            </text>
            <text
              x={outputX + 60}
              y={(Math.min(...outYs) + Math.max(...outYs)) / 2 + 35}
              textAnchor="middle"
              fontSize="12"
              fill={statusColor}
              fontWeight="700"
            >
              {output.label}
            </text>
            <text
              x={outputX + 60}
              y={(Math.min(...outYs) + Math.max(...outYs)) / 2 + 50}
              textAnchor="middle"
              fontSize="10"
              fill="#64748b"
            >
              {output.confidence}% confidence
            </text>
          </g>

          {/* Layer labels */}
          <text
            x={inputX}
            y={inYs[0] - 30}
            textAnchor="middle"
            fontSize="10"
            fill="#64748b"
            fontWeight="500"
          >
            Input
          </text>
          <text
            x={hidden1X}
            y={h1Ys[0] - 30}
            textAnchor="middle"
            fontSize="10"
            fill="#64748b"
            fontWeight="500"
          >
            Hidden 1
          </text>
          <text
            x={hidden2X}
            y={h2Ys[0] - 30}
            textAnchor="middle"
            fontSize="10"
            fill="#64748b"
            fontWeight="500"
          >
            Hidden 2
          </text>
          <text
            x={outputX}
            y={outYs[0] - 30}
            textAnchor="middle"
            fontSize="10"
            fill="#64748b"
            fontWeight="500"
          >
            Output
          </text>

          {/* Connections with noise effect */}
          {inYs.map((_, i) =>
            h1Ys.map((_, j) => (
              <line
                key={`in-h1-${i}-${j}`}
                x1={inputX + 12}
                y1={inYs[i]}
                x2={hidden1X - 12}
                y2={h1Ys[j]}
                stroke={noiseLevel > 0.5 ? "#fca5a5" : "#cbd5e1"}
                strokeWidth={noiseLevel > 0.5 ? "1.5" : "1"}
                opacity={noiseLevel > 0 ? 0.3 + noiseLevel * 0.3 : 0.3}
                strokeDasharray={noiseLevel > 0.7 ? "3,3" : "0"}
              />
            ))
          )}
          {h1Ys.map((_, i) =>
            h2Ys.map((_, j) => (
              <line
                key={`h1-h2-${i}-${j}`}
                x1={hidden1X + 12}
                y1={h1Ys[i]}
                x2={hidden2X - 12}
                y2={h2Ys[j]}
                stroke={noiseLevel > 0.5 ? "#fca5a5" : "#cbd5e1"}
                strokeWidth={noiseLevel > 0.5 ? "1.5" : "1"}
                opacity={noiseLevel > 0 ? 0.3 + noiseLevel * 0.3 : 0.3}
                strokeDasharray={noiseLevel > 0.7 ? "3,3" : "0"}
              />
            ))
          )}
          {h2Ys.map((_, i) =>
            outYs.map((_, j) => (
              <line
                key={`h2-out-${i}-${j}`}
                x1={hidden2X + 12}
                y1={h2Ys[i]}
                x2={outputX - 12}
                y2={outYs[j]}
                stroke={noiseLevel > 0.5 ? "#fca5a5" : "#cbd5e1"}
                strokeWidth={noiseLevel > 0.5 ? "1.5" : "1"}
                opacity={noiseLevel > 0 ? 0.3 + noiseLevel * 0.3 : 0.3}
                strokeDasharray={noiseLevel > 0.7 ? "3,3" : "0"}
              />
            ))
          )}

          {/* Neurons */}
          {inYs.map((y, i) => (
            <circle
              key={`in-${i}`}
              cx={inputX}
              cy={y}
              r="12"
              fill="white"
              stroke={noiseLevel > 0.5 ? "#f97316" : "#8b5cf6"}
              strokeWidth="2"
            />
          ))}
          {h1Ys.map((y, i) => (
            <circle
              key={`h1-${i}`}
              cx={hidden1X}
              cy={y}
              r="12"
              fill="white"
              stroke={noiseLevel > 0.5 ? "#f97316" : "#8b5cf6"}
              strokeWidth="2"
            />
          ))}
          {h2Ys.map((y, i) => (
            <circle
              key={`h2-${i}`}
              cx={hidden2X}
              cy={y}
              r="12"
              fill="white"
              stroke={noiseLevel > 0.5 ? "#f97316" : "#8b5cf6"}
              strokeWidth="2"
            />
          ))}
          {outYs.map((y, i) => (
            <circle
              key={`out-${i}`}
              cx={outputX}
              cy={y}
              r="12"
              fill="white"
              stroke={noiseLevel > 0.5 ? "#ef4444" : "#10b981"}
              strokeWidth="2"
            />
          ))}

          {/* Noise particles floating around */}
          {noiseParticles.map((particle) => (
            <circle
              key={`noise-${particle.id}`}
              cx={particle.x}
              cy={particle.y}
              r="2"
              fill="#ef4444"
              opacity="0.4"
            >
              <animate
                attributeName="cy"
                values={`${particle.y};${particle.y - 20};${particle.y}`}
                dur={`${2 + Math.random() * 2}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0.4;0.8;0.4"
                dur={`${1 + Math.random()}s`}
                repeatCount="indefinite"
              />
            </circle>
          ))}

          {/* Controls and metrics */}
          <foreignObject x={30} y={controlsY} width={width - 60} height={320}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                padding: "20px",
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              {/* Metrics display */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                {/* Accuracy */}
                <div
                  style={{
                    background: `linear-gradient(135deg, ${statusColor}20, ${statusColor}10)`,
                    border: `2px solid ${statusColor}`,
                    borderRadius: "10px",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      marginBottom: "8px",
                    }}
                  >
                    Model Accuracy
                  </div>
                  <div
                    style={{
                      fontSize: "36px",
                      fontWeight: "700",
                      color: statusColor,
                      lineHeight: "1",
                    }}
                  >
                    {accuracy.toFixed(0)}%
                  </div>
                </div>

                {/* Signal-to-Noise Ratio */}
                <div
                  style={{
                    background: "linear-gradient(135deg, #8b5cf620, #8b5cf610)",
                    border: "2px solid #8b5cf6",
                    borderRadius: "10px",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      marginBottom: "8px",
                    }}
                  >
                    Signal-to-Noise Ratio
                  </div>
                  <div
                    style={{
                      fontSize: "36px",
                      fontWeight: "700",
                      color: "#8b5cf6",
                      lineHeight: "1",
                    }}
                  >
                    {snr}%
                  </div>
                </div>
              </div>

              {/* Noise control slider */}
              <div
                style={{
                  background: "white",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "16px",
                }}
              >
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#475569",
                    marginBottom: "12px",
                    textAlign: "center",
                  }}
                >
                  Noise Level: {(noiseLevel * 100).toFixed(0)}%
                </label>

                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={noiseLevel}
                  onChange={(e) => setNoiseLevel(parseFloat(e.target.value))}
                  style={{
                    width: "100%",
                    height: "8px",
                    cursor: "pointer",
                    borderRadius: "4px",
                    background: `linear-gradient(to right, #10b981 0%, #f59e0b 50%, #ef4444 100%)`,
                    outline: "none",
                    WebkitAppearance: "none",
                    appearance: "none",
                  }}
                />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "8px",
                    fontSize: "10px",
                    color: "#94a3b8",
                  }}
                >
                  <span>Clean</span>
                  <span>Moderate</span>
                  <span>High Noise</span>
                </div>

                {/* Status indicator */}
                <div
                  style={{
                    marginTop: "12px",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    backgroundColor: statusBg,
                    textAlign: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: statusColor,
                    }}
                  >
                    {noiseLevel === 0
                      ? "✓ Perfect Signal - No Noise"
                      : noiseLevel < 0.3
                      ? "✓ Low Noise - Good Quality"
                      : noiseLevel < 0.6
                      ? "⚠ Moderate Noise - Degraded Performance"
                      : "⚠ High Noise - Poor Quality"}
                  </span>
                </div>
              </div>

              {/* Explanation */}
              <div
                style={{
                  padding: "12px 14px",
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: "8px",
                  fontSize: "11px",
                  color: "#1e40af",
                  lineHeight: "1.5",
                }}
              >
                <strong>💡 Noise Impact:</strong> Noise in input data corrupts
                the signal, making it harder for neural networks to extract
                meaningful patterns. High noise reduces accuracy and confidence.
                Real-world applications often require noise filtering or data
                augmentation to improve robustness.
              </div>
            </div>
          </foreignObject>

          {/* Slider thumb styling */}
          <defs>
            <style>{`
          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: white;
            border: 3px solid #8b5cf6;
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
          input[type="range"]::-moz-range-thumb {
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: white;
            border: 3px solid #8b5cf6;
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
        `}</style>
          </defs>
        </svg>
      );
    }

    if (concept.id === "dropout") {
      const layerCounts = [3, 3, 4, 3];
      const maxCount = Math.max(...layerCounts);
      const inYs = layerYs(layerCounts[0], maxCount);
      const h1Ys = layerYs(layerCounts[1], maxCount);
      const h2Ys = layerYs(layerCounts[2], maxCount);
      const outYs = layerYs(layerCounts[3], maxCount);
      const controlsY = controlsYFor(maxCount);

      const disabledIndices = userDropped;

      // Helper to check disabled by global index mapping
      const isDisabled = (layerOffset, idx) =>
        disabledIndices.includes(layerOffset + idx);

      // Count disabled neurons per layer
      const inputDisabledCount = [0, 1, 2].filter((i) =>
        isDisabled(0, i)
      ).length;
      const h1DisabledCount = [0, 1, 2].filter((i) => isDisabled(3, i)).length;
      const h2DisabledCount = [0, 1, 2, 3].filter((i) =>
        isDisabled(6, i)
      ).length;

      // Check if we can disable more neurons in a layer (at least 1 must remain active)
      const canDisableInLayer = (layerOffset, layerSize) => {
        const disabledInLayer = Array.from(
          { length: layerSize },
          (_, i) => i
        ).filter((i) => isDisabled(layerOffset, i)).length;
        return disabledInLayer < layerSize - 1; // Keep at least 1 active
      };

      // Calculate training vs testing accuracy
      // Dropout improves generalization (testing accuracy) by preventing overfitting
      const baseTrainingAcc = 98; // High training accuracy (potential overfitting without dropout)
      const baseTestingAcc = 75; // Lower testing accuracy without dropout

      const dropoutRate = disabledIndices.length / 10; // 0 to 1 (max 10 neurons total)

      // With dropout: training accuracy slightly decreases, testing accuracy improves
      const trainingAccuracy = Math.max(85, baseTrainingAcc - dropoutRate * 15);
      const testingAccuracy = Math.min(90, baseTestingAcc + dropoutRate * 20);

      // Generalization gap (smaller is better)
      const generalizationGap = trainingAccuracy - testingAccuracy;

      return (
        <svg
          width={width}
          height={Math.max(height, controlsY + 340)}
          viewBox={`0 0 ${width} ${Math.max(height, controlsY + 340)}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: "visible" }}
        >
          <text
            x={width / 2}
            y={40}
            textAnchor="middle"
            fill="#8b5cf6"
            fontSize="16"
            fontWeight="600"
          >
            Dropout Regularization: Preventing Overfitting
          </text>

          <text
            x={width / 2}
            y={60}
            textAnchor="middle"
            fill="#64748b"
            fontSize="12"
          >
            Click neurons to temporarily drop them during training (max 1 per
            layer active)
          </text>

          {/* Layer labels */}
          <text
            x={inputX}
            y={inYs[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Input
          </text>
          <text
            x={hidden1X}
            y={h1Ys[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Hidden 1
          </text>
          <text
            x={hidden2X}
            y={h2Ys[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Hidden 2
          </text>
          <text
            x={outputX}
            y={outYs[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Output
          </text>

          {/* Connections */}
          {inYs.map((_, i) =>
            h1Ys.map((_, j) => {
              if (isDisabled(0, i) || isDisabled(3, j)) return null;
              return (
                <line
                  key={`in-h1-${i}-${j}`}
                  x1={inputX + 12}
                  y1={inYs[i]}
                  x2={hidden1X - 12}
                  y2={h1Ys[j]}
                  stroke="#cbd5e1"
                  strokeWidth="1"
                  opacity="0.3"
                />
              );
            })
          )}
          {h1Ys.map((_, i) =>
            h2Ys.map((_, j) => {
              if (isDisabled(3, i) || isDisabled(6, j)) return null;
              return (
                <line
                  key={`h1-h2-${i}-${j}`}
                  x1={hidden1X + 12}
                  y1={h1Ys[i]}
                  x2={hidden2X - 12}
                  y2={h2Ys[j]}
                  stroke="#cbd5e1"
                  strokeWidth="1"
                  opacity="0.3"
                />
              );
            })
          )}
          {h2Ys.map((_, i) =>
            outYs.map((_, j) => {
              if (isDisabled(6, i)) return null;
              return (
                <line
                  key={`h2-out-${i}-${j}`}
                  x1={hidden2X + 12}
                  y1={h2Ys[i]}
                  x2={outputX - 12}
                  y2={outYs[j]}
                  stroke="#cbd5e1"
                  strokeWidth="1"
                  opacity="0.3"
                />
              );
            })
          )}

          {/* Input Layer */}
          {inYs.map((y, i) => {
            const disabled = isDisabled(0, i);
            const canDisable = canDisableInLayer(0, 3);
            const canToggle = disabled || canDisable;

            return (
              <g
                key={`in-${i}`}
                style={{ cursor: canToggle ? "pointer" : "not-allowed" }}
                onClick={() => {
                  if (!canToggle) return;
                  const id = i;
                  if (disabled) {
                    setUserDropped(disabledIndices.filter((d) => d !== id));
                  } else if (canDisable && disabledIndices.length < 10) {
                    setUserDropped([...disabledIndices, id]);
                  }
                }}
              >
                <circle
                  cx={inputX}
                  cy={y}
                  r="12"
                  fill={disabled ? "#fee2e2" : "white"}
                  stroke={disabled ? "#f97316" : "#8b5cf6"}
                  strokeWidth="2"
                  opacity={disabled ? "0.5" : "1"}
                />
                {disabled && (
                  <text
                    x={inputX}
                    y={y + 4}
                    textAnchor="middle"
                    fontSize="16"
                    fontWeight="bold"
                    fill="#f97316"
                  >
                    ⊘
                  </text>
                )}
                {/* Hover effect */}
                {!disabled && canToggle && (
                  <circle
                    cx={inputX}
                    cy={y}
                    r="16"
                    fill="none"
                    stroke="#8b5cf6"
                    strokeWidth="1"
                    opacity="0"
                    style={{ transition: "opacity 0.2s" }}
                    className="hover-ring"
                  />
                )}
              </g>
            );
          })}

          {/* Hidden Layer 1 */}
          {h1Ys.map((y, i) => {
            const disabled = isDisabled(3, i);
            const canDisable = canDisableInLayer(3, 3);
            const canToggle = disabled || canDisable;

            return (
              <g
                key={`h1-${i}`}
                style={{ cursor: canToggle ? "pointer" : "not-allowed" }}
                onClick={() => {
                  if (!canToggle) return;
                  const id = 3 + i;
                  if (disabled) {
                    setUserDropped(disabledIndices.filter((d) => d !== id));
                  } else if (canDisable && disabledIndices.length < 10) {
                    setUserDropped([...disabledIndices, id]);
                  }
                }}
              >
                <circle
                  cx={hidden1X}
                  cy={y}
                  r="12"
                  fill={disabled ? "#fee2e2" : "white"}
                  stroke={disabled ? "#f97316" : "#8b5cf6"}
                  strokeWidth="2"
                  opacity={disabled ? "0.5" : "1"}
                />
                {disabled && (
                  <text
                    x={hidden1X}
                    y={y + 4}
                    textAnchor="middle"
                    fontSize="16"
                    fontWeight="bold"
                    fill="#f97316"
                  >
                    ⊘
                  </text>
                )}
              </g>
            );
          })}

          {/* Hidden Layer 2 */}
          {h2Ys.map((y, i) => {
            const disabled = isDisabled(6, i);
            const canDisable = canDisableInLayer(6, 4);
            const canToggle = disabled || canDisable;

            return (
              <g
                key={`h2-${i}`}
                style={{ cursor: canToggle ? "pointer" : "not-allowed" }}
                onClick={() => {
                  if (!canToggle) return;
                  const id = 6 + i;
                  if (disabled) {
                    setUserDropped(disabledIndices.filter((d) => d !== id));
                  } else if (canDisable && disabledIndices.length < 10) {
                    setUserDropped([...disabledIndices, id]);
                  }
                }}
              >
                <circle
                  cx={hidden2X}
                  cy={y}
                  r="12"
                  fill={disabled ? "#fee2e2" : "white"}
                  stroke={disabled ? "#f97316" : "#8b5cf6"}
                  strokeWidth="2"
                  opacity={disabled ? "0.5" : "1"}
                />
                {disabled && (
                  <text
                    x={hidden2X}
                    y={y + 4}
                    textAnchor="middle"
                    fontSize="16"
                    fontWeight="bold"
                    fill="#f97316"
                  >
                    ⊘
                  </text>
                )}
              </g>
            );
          })}

          {/* Output Layer (cannot be dropped) */}
          {outYs.map((y, i) => (
            <circle
              key={`out-${i}`}
              cx={outputX}
              cy={y}
              r="12"
              fill="white"
              stroke="#10b981"
              strokeWidth="2"
            />
          ))}

          {/* Controls and metrics */}
          <foreignObject x={30} y={controlsY} width={width - 60} height={340}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                padding: "20px",
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              {/* Accuracy comparison */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                {/* Training Accuracy */}
                <div
                  style={{
                    background: "linear-gradient(135deg, #3b82f620, #3b82f610)",
                    border: "2px solid #3b82f6",
                    borderRadius: "10px",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      marginBottom: "8px",
                    }}
                  >
                    📚 Training Accuracy
                  </div>
                  <div
                    style={{
                      fontSize: "36px",
                      fontWeight: "700",
                      color: "#3b82f6",
                      lineHeight: "1",
                    }}
                  >
                    {trainingAccuracy.toFixed(0)}%
                  </div>
                </div>

                {/* Testing Accuracy */}
                <div
                  style={{
                    background: "linear-gradient(135deg, #10b98120, #10b98110)",
                    border: "2px solid #10b981",
                    borderRadius: "10px",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      marginBottom: "8px",
                    }}
                  >
                    🎯 Testing Accuracy
                  </div>
                  <div
                    style={{
                      fontSize: "36px",
                      fontWeight: "700",
                      color: "#10b981",
                      lineHeight: "1",
                    }}
                  >
                    {testingAccuracy.toFixed(0)}%
                  </div>
                </div>
              </div>

              {/* Generalization gap */}
              <div
                style={{
                  background:
                    generalizationGap < 10
                      ? "#d1fae5"
                      : generalizationGap < 20
                      ? "#fef3c7"
                      : "#fee2e2",
                  border: `2px solid ${
                    generalizationGap < 10
                      ? "#10b981"
                      : generalizationGap < 20
                      ? "#f59e0b"
                      : "#ef4444"
                  }`,
                  borderRadius: "10px",
                  padding: "12px 16px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#64748b",
                    marginBottom: "4px",
                  }}
                >
                  Generalization Gap (Lower is Better)
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    color:
                      generalizationGap < 10
                        ? "#10b981"
                        : generalizationGap < 20
                        ? "#f59e0b"
                        : "#ef4444",
                  }}
                >
                  {generalizationGap.toFixed(1)}%
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    marginTop: "4px",
                  }}
                >
                  {generalizationGap < 10
                    ? "✓ Good generalization"
                    : generalizationGap < 20
                    ? "⚠ Moderate overfitting"
                    : "⚠ High overfitting"}
                </div>
              </div>

              {/* Status */}
              <div
                style={{
                  background: "white",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "14px",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#475569",
                    marginBottom: "10px",
                  }}
                >
                  Dropout Status:
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "11px",
                    color: "#64748b",
                    marginBottom: "10px",
                  }}
                >
                  <span>
                    Total dropped: <strong>{disabledIndices.length}/10</strong>
                  </span>
                  <span>
                    Input: <strong>{inputDisabledCount}/3</strong> | H1:{" "}
                    <strong>{h1DisabledCount}/3</strong> | H2:{" "}
                    <strong>{h2DisabledCount}/4</strong>
                  </span>
                </div>

                <button
                  onClick={() => setUserDropped([])}
                  disabled={disabledIndices.length === 0}
                  style={{
                    width: "100%",
                    padding: "10px 16px",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: disabledIndices.length === 0 ? "#cbd5e1" : "white",
                    background:
                      disabledIndices.length === 0
                        ? "#f1f5f9"
                        : "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                    border: "none",
                    borderRadius: "6px",
                    cursor:
                      disabledIndices.length === 0 ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                    opacity: disabledIndices.length === 0 ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (disabledIndices.length > 0) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 8px rgba(139, 92, 246, 0.3)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (disabledIndices.length > 0) {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }
                  }}
                >
                  🔄 Clear All Dropout
                </button>
              </div>

              {/* Explanation */}
              <div
                style={{
                  padding: "12px 14px",
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: "8px",
                  fontSize: "11px",
                  color: "#1e40af",
                  lineHeight: "1.5",
                }}
              >
                <strong>💡 How Dropout Works:</strong> During training, randomly
                drop neurons to prevent over-reliance on specific features. This
                reduces overfitting and improves generalization to new data.
                Notice how testing accuracy improves as you add dropout, even
                though training accuracy slightly decreases.
              </div>
            </div>
          </foreignObject>

          <style>{`
        .hover-ring:hover {
          opacity: 0.3 !important;
        }
      `}</style>
        </svg>
      );
    }

    if (concept.id === "ablation") {
      const layerCounts = [3, 3, 3, 3];
      const maxCount = Math.max(...layerCounts);
      const inYs = layerYs(layerCounts[0], maxCount);
      const h1Ys = layerYs(layerCounts[1], maxCount);
      const h2Ys = layerYs(layerCounts[2], maxCount);
      const outYs = layerYs(layerCounts[3], maxCount);
      const controlsY = controlsYFor(maxCount);

      // Track removed neurons (only from hidden and output layers)
      // animationStep represents the number of ablation steps (0-3)
      const ablationStep = Math.floor(animationStep) % 4; // 0, 1, 2, or 3

      // Generate consistent removed neurons based on step

      let cachedOrder = null; // persist stable removal order

      // Helper function to provide stable, seeded pseudo-randomness
      const pseudoRandom = (seed) => {
        const x = Math.sin(seed) * 10000;
        return x - Math.floor(x);
      };

      const getRemovedNeurons = (step) => {
        if (step === 0) return [];

        // 🔒 Create a stable shuffled order ONCE
        if (!cachedOrder) {
          const layers = ["h1", "h2", "out"];
          const countPerLayer = 3;
          let seed = 9999; // Starting seed for deterministic shuffle

          // 1. Create a pool of randomized indices for EACH layer independently
          const layerPools = {};

          layers.forEach((layer) => {
            const indices = [0, 1, 2];

            // Shuffle indices for this specific layer using the seed
            for (let i = indices.length - 1; i > 0; i--) {
              seed += 6789; // Increment seed for next random number
              const randomVal = pseudoRandom(seed);
              const j = Math.floor(randomVal * (i + 1));
              [indices[i], indices[j]] = [indices[j], indices[i]];
            }

            layerPools[layer] = indices;
          });

          // 2. Build the final order using Round-Robin (h1 -> h2 -> out -> h1...)
          const interleaved = [];

          for (let i = 0; i < countPerLayer; i++) {
            layers.forEach((layer) => {
              interleaved.push({
                layer: layer,
                idx: layerPools[layer][i],
              });
            });
          }

          cachedOrder = interleaved; // store permanently
        }

        // ✔️ The removal order is stable and sequential, ensuring previous steps don't change.
        return cachedOrder.slice(0, step);
      };

      const removedNeurons = getRemovedNeurons(ablationStep);

      // Check if a neuron is removed
      const isH1Removed = (i) =>
        removedNeurons.some((n) => n.layer === "h1" && n.idx === i);
      const isH2Removed = (i) =>
        removedNeurons.some((n) => n.layer === "h2" && n.idx === i);
      const isOutRemoved = (i) =>
        removedNeurons.some((n) => n.layer === "out" && n.idx === i);

      // Calculate accuracy based on removed neurons
      const baseAccuracy = 94;
      const accuracyDrop = ablationStep * 15; // Each ablation drops accuracy by ~15%
      const currentAccuracy = Math.max(25, baseAccuracy - accuracyDrop);

      const maxAblations = 3;
      const canRemoveMore = ablationStep < maxAblations;

      return (
        <svg
          width={width}
          height={Math.max(height, controlsY + 300)}
          viewBox={`0 0 ${width} ${Math.max(height, controlsY + 300)}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: "visible" }}
        >
          <text
            x={width / 2}
            y={40}
            textAnchor="middle"
            fill="#8b5cf6"
            fontSize="16"
            fontWeight="600"
          >
            Ablation Study: Understanding Layer Importance
          </text>

          <text
            x={width / 2}
            y={60}
            textAnchor="middle"
            fill="#64748b"
            fontSize="12"
          >
            Remove neurons from hidden/output layers to measure their impact
          </text>

          {/* Layer labels */}
          <text
            x={inputX}
            y={inYs[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Input (Protected)
          </text>
          <text
            x={hidden1X}
            y={h1Ys[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Hidden 1
          </text>
          <text
            x={hidden2X}
            y={h2Ys[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Hidden 2
          </text>
          <text
            x={outputX}
            y={outYs[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Output
          </text>

          {/* Connections */}
          {inYs.map((_, i) =>
            h1Ys.map((_, j) => {
              const disabled = isH1Removed(j);
              return (
                <line
                  key={`in-h1-${i}-${j}`}
                  x1={inputX + 12}
                  y1={inYs[i]}
                  x2={hidden1X - 12}
                  y2={h1Ys[j]}
                  stroke={disabled ? "#fecaca" : "#cbd5e1"}
                  strokeWidth="1"
                  opacity={disabled ? "0.2" : "0.3"}
                  strokeDasharray={disabled ? "3,3" : "0"}
                />
              );
            })
          )}
          {h1Ys.map((_, i) =>
            h2Ys.map((_, j) => {
              const disabled = isH1Removed(i) || isH2Removed(j);
              return (
                <line
                  key={`h1-h2-${i}-${j}`}
                  x1={hidden1X + 12}
                  y1={h1Ys[i]}
                  x2={hidden2X - 12}
                  y2={h2Ys[j]}
                  stroke={disabled ? "#fecaca" : "#cbd5e1"}
                  strokeWidth="1"
                  opacity={disabled ? "0.2" : "0.3"}
                  strokeDasharray={disabled ? "3,3" : "0"}
                />
              );
            })
          )}
          {h2Ys.map((_, i) =>
            outYs.map((_, j) => {
              const disabled = isH2Removed(i) || isOutRemoved(j);
              return (
                <line
                  key={`h2-out-${i}-${j}`}
                  x1={hidden2X + 12}
                  y1={h2Ys[i]}
                  x2={outputX - 12}
                  y2={outYs[j]}
                  stroke={disabled ? "#fecaca" : "#cbd5e1"}
                  strokeWidth="1"
                  opacity={disabled ? "0.2" : "0.3"}
                  strokeDasharray={disabled ? "3,3" : "0"}
                />
              );
            })
          )}

          {/* Input Layer - Always active (not removable) */}
          {inYs.map((y, i) => (
            <g key={`in-${i}`}>
              <circle
                cx={inputX}
                cy={y}
                r="12"
                fill="white"
                stroke="#10b981"
                strokeWidth="2"
              />
              <circle
                cx={inputX}
                cy={y}
                r="16"
                fill="none"
                stroke="#10b981"
                strokeWidth="1"
                opacity="0.3"
              />
            </g>
          ))}

          {/* Hidden Layer 1 */}
          {h1Ys.map((y, i) => {
            const removed = isH1Removed(i);
            return (
              <g key={`h1-${i}`}>
                <circle
                  cx={hidden1X}
                  cy={y}
                  r="12"
                  fill={removed ? "#fee2e2" : "white"}
                  stroke={removed ? "#ef4444" : "#8b5cf6"}
                  strokeWidth="2"
                  opacity={removed ? "0.4" : "1"}
                />
                {removed && (
                  <>
                    <line
                      x1={hidden1X - 8}
                      y1={y - 8}
                      x2={hidden1X + 8}
                      y2={y + 8}
                      stroke="#ef4444"
                      strokeWidth="2.5"
                    />
                    <line
                      x1={hidden1X - 8}
                      y1={y + 8}
                      x2={hidden1X + 8}
                      y2={y - 8}
                      stroke="#ef4444"
                      strokeWidth="2.5"
                    />
                  </>
                )}
              </g>
            );
          })}

          {/* Hidden Layer 2 */}
          {h2Ys.map((y, i) => {
            const removed = isH2Removed(i);
            return (
              <g key={`h2-${i}`}>
                <circle
                  cx={hidden2X}
                  cy={y}
                  r="12"
                  fill={removed ? "#fee2e2" : "white"}
                  stroke={removed ? "#ef4444" : "#8b5cf6"}
                  strokeWidth="2"
                  opacity={removed ? "0.4" : "1"}
                />
                {removed && (
                  <>
                    <line
                      x1={hidden2X - 8}
                      y1={y - 8}
                      x2={hidden2X + 8}
                      y2={y + 8}
                      stroke="#ef4444"
                      strokeWidth="2.5"
                    />
                    <line
                      x1={hidden2X - 8}
                      y1={y + 8}
                      x2={hidden2X + 8}
                      y2={y - 8}
                      stroke="#ef4444"
                      strokeWidth="2.5"
                    />
                  </>
                )}
              </g>
            );
          })}

          {/* Output Layer */}
          {outYs.map((y, i) => {
            const removed = isOutRemoved(i);
            return (
              <g key={`out-${i}`}>
                <circle
                  cx={outputX}
                  cy={y}
                  r="12"
                  fill={removed ? "#fee2e2" : "white"}
                  stroke={removed ? "#ef4444" : "#8b5cf6"}
                  strokeWidth="2"
                  opacity={removed ? "0.4" : "1"}
                />
                {removed && (
                  <>
                    <line
                      x1={outputX - 8}
                      y1={y - 8}
                      x2={outputX + 8}
                      y2={y + 8}
                      stroke="#ef4444"
                      strokeWidth="2.5"
                    />
                    <line
                      x1={outputX - 8}
                      y1={y + 8}
                      x2={outputX + 8}
                      y2={y - 8}
                      stroke="#ef4444"
                      strokeWidth="2.5"
                    />
                  </>
                )}
              </g>
            );
          })}

          {/* Controls and information */}
          <foreignObject x={30} y={controlsY} width={width - 60} height={300}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                padding: "20px",
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              {/* Accuracy display */}
              <div
                style={{
                  background: "linear-gradient(135deg, #10b98120, #10b98110)",
                  border: "2px solid #10b981",
                  borderRadius: "12px",
                  padding: "20px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "48px",
                    fontWeight: "700",
                    color:
                      currentAccuracy > 70
                        ? "#10b981"
                        : currentAccuracy > 40
                        ? "#f59e0b"
                        : "#ef4444",
                    lineHeight: "1",
                    marginBottom: "8px",
                  }}
                >
                  {currentAccuracy}%
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#64748b",
                  }}
                >
                  Model Accuracy
                </div>
              </div>

              {/* Status and controls */}
              <div
                style={{
                  background: "white",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "16px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                    marginBottom: "14px",
                  }}
                >
                  <div
                    style={{
                      padding: "10px",
                      background: "#f8fafc",
                      borderRadius: "6px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#64748b",
                        marginBottom: "4px",
                      }}
                    >
                      Ablation Step
                    </div>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#8b5cf6",
                      }}
                    >
                      {ablationStep} / {maxAblations}
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "10px",
                      background: "#f8fafc",
                      borderRadius: "6px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#64748b",
                        marginBottom: "4px",
                      }}
                    >
                      Neurons Removed
                    </div>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#ef4444",
                      }}
                    >
                      {removedNeurons.length}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px",
                  }}
                >
                  <button
                    onClick={() => {
                      if (canRemoveMore) {
                        setAnimationStep((p) => Math.floor(p) + 1);
                      }
                    }}
                    disabled={!canRemoveMore}
                    style={{
                      padding: "12px 20px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "white",
                      background: canRemoveMore
                        ? "linear-gradient(135deg, #ef4444, #dc2626)"
                        : "#cbd5e1",
                      border: "none",
                      borderRadius: "8px",
                      cursor: canRemoveMore ? "pointer" : "not-allowed",
                      transition: "all 0.2s ease",
                      boxShadow: canRemoveMore
                        ? "0 2px 4px rgba(0,0,0,0.1)"
                        : "none",
                      opacity: canRemoveMore ? 1 : 0.6,
                    }}
                    onMouseEnter={(e) => {
                      if (canRemoveMore) {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 8px rgba(239, 68, 68, 0.3)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (canRemoveMore) {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 4px rgba(0,0,0,0.1)";
                      }
                    }}
                  >
                    {canRemoveMore ? "❌ Remove Neuron" : "⚠️ Max Reached"}
                  </button>

                  <button
                    onClick={() => {
                      setAnimationStep(0);
                    }}
                    style={{
                      padding: "12px 20px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#64748b",
                      background: "white",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#10b981";
                      e.currentTarget.style.color = "#10b981";
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e2e8f0";
                      e.currentTarget.style.color = "#64748b";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    🔄 Reset
                  </button>
                </div>
              </div>

              {/* Explanation */}
              <div
                style={{
                  padding: "14px 16px",
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: "8px",
                  fontSize: "11px",
                  color: "#1e40af",
                  lineHeight: "1.5",
                }}
              >
                <strong>💡 Ablation Study:</strong> Systematically remove
                neurons from hidden and output layers to identify which
                components are critical for performance. Input neurons are
                protected as they represent the data itself. Significant
                accuracy drops reveal important neurons.
              </div>
            </div>
          </foreignObject>
        </svg>
      );
    }

    if (concept.id === "backprop") {
      const layerCounts = [2, 3, 4, 3];
      const maxCount = Math.max(...layerCounts);
      const inYs = layerYs(layerCounts[0], maxCount);
      const h1Ys = layerYs(layerCounts[1], maxCount);
      const h2Ys = layerYs(layerCounts[2], maxCount);
      const outYs = layerYs(layerCounts[3], maxCount);
      const controlsY = controlsYFor(maxCount);

      const p = backpropProgress;

      // --- TIMING CONSTANTS ---
      const FWD_1_START = 0.0,
        FWD_1_END = 0.15; // Input -> H1
      const FWD_2_START = 0.15,
        FWD_2_END = 0.3; // H1 -> H2
      const FWD_3_START = 0.3,
        FWD_3_END = 0.45; // H2 -> Output
      const ERR_START = 0.45,
        ERR_END = 0.55; // Error computation
      const BWD_1_START = 0.55,
        BWD_1_END = 0.7; // Output -> H2 (Update W3)
      const BWD_2_START = 0.7,
        BWD_2_END = 0.85; // H2 -> H1 (Update W2)
      const BWD_3_START = 0.85,
        BWD_3_END = 1.0; // H1 -> Input (Update W1)

      // --- HELPERS ---
      const isActive = (start, end) => p >= start && p < end;
      const getSubProgress = (start, end) =>
        Math.max(0, Math.min(1, (p - start) / (end - start)));

      // Active stages
      const showInputToH1 = isActive(FWD_1_START, FWD_1_END);
      const showH1ToH2 = isActive(FWD_2_START, FWD_2_END);
      const showH2ToOut = isActive(FWD_3_START, FWD_3_END);
      const showError = p >= ERR_START;
      const showOutToH2 = isActive(BWD_1_START, BWD_1_END);
      const showH2ToH1 = isActive(BWD_2_START, BWD_2_END);
      const showH1ToIn = isActive(BWD_3_START, BWD_3_END);

      // Check which weights are being updated
      const updatingW3 = p >= BWD_1_START && p < BWD_1_END;
      const updatingW2 = p >= BWD_2_START && p < BWD_2_END;
      const updatingW1 = p >= BWD_3_START && p <= BWD_3_END;

      // --- RENDERING FUNCTIONS ---

      // Render traveling dots FROM ALL NEURONS
      const renderTravelingDots = (
        fromYs,
        toYs,
        xFrom,
        xTo,
        progress,
        color
      ) => {
        if (progress <= 0 || progress >= 1) return null;

        const dots = [];
        // Show dots from EVERY neuron connection
        fromYs.forEach((yf, i) => {
          toYs.forEach((yt, j) => {
            const cx = xFrom + (xTo - xFrom) * progress;
            const cy = yf + (yt - yf) * progress;

            dots.push(
              <circle
                key={`dot-${i}-${j}`}
                cx={cx}
                cy={cy}
                r="3"
                fill={color}
                opacity="0.7"
              />
            );
          });
        });

        return <g>{dots}</g>;
      };

      // Render connections with weight update highlighting
      const renderConnectionsWithUpdate = (x1, ys1, x2, ys2, isUpdating) => {
        return ys1.map((y1, i) =>
          ys2.map((y2, j) => (
            <line
              key={`conn-${i}-${j}`}
              x1={x1 + 12}
              y1={y1}
              x2={x2 - 12}
              y2={y2}
              stroke={isUpdating ? "#f97316" : "#cbd5e1"}
              strokeWidth={isUpdating ? "2.5" : "1"}
              opacity={isUpdating ? "0.8" : "0.3"}
              style={{
                transition: "all 0.3s ease",
              }}
            />
          ))
        );
      };

      // Generate pseudo-random but consistent weights for neurons
      const getWeight = (layerIdx, neuronIdx) => {
        const seed = layerIdx * 10 + neuronIdx;
        return (0.3 + (seed % 5) * 0.15).toFixed(2);
      };

      // Render neuron with weight value inside
      // Render neuron with weight value inside
      const renderNeuronWithWeight = (x, y, layerIdx, neuronIdx, active) => {
        const baseWeight = getWeight(layerIdx, neuronIdx);

        // Calculate updated weight during backward pass
        let displayWeight = baseWeight;
        if (layerIdx === 1 && p >= BWD_3_START) {
          // W1 has been updated
          displayWeight = (parseFloat(baseWeight) + 0.05).toFixed(2);
        } else if (layerIdx === 2 && p >= BWD_2_START) {
          // W2 has been updated
          displayWeight = (parseFloat(baseWeight) - 0.03).toFixed(2);
        } else if (layerIdx === 3 && p >= BWD_1_START) {
          // W3 has been updated
          displayWeight = (parseFloat(baseWeight) + 0.07).toFixed(2);
        }

        const isBeingUpdated =
          (layerIdx === 1 && updatingW1) ||
          (layerIdx === 2 && updatingW2) ||
          (layerIdx === 3 && updatingW3);

        return (
          <g key={`neuron-${layerIdx}-${neuronIdx}`}>
            {/* Neuron circle */}
            <circle
              cx={x}
              cy={y}
              r="12"
              fill="white"
              stroke={active ? "#8b5cf6" : "#cbd5e1"}
              strokeWidth="2"
            />

            {/* Pulsing ring when weight is being updated */}
            {isBeingUpdated && (
              <circle
                cx={x}
                cy={y}
                r="12"
                fill="none"
                stroke="#f97316"
                strokeWidth="2"
                opacity="0.6"
              >
                <animate
                  attributeName="r"
                  values="12;18;12"
                  dur="1s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0.6;0;0.6"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            )}

            {/* Weight value inside neuron - ONLY for layers > 0 (not input layer) */}
            {layerIdx > 0 && (
              <text
                x={x}
                y={y + 4}
                textAnchor="middle"
                fontSize="9"
                fontWeight="600"
                fill={isBeingUpdated ? "#f97316" : "#64748b"}
              >
                {displayWeight}
              </text>
            )}
          </g>
        );
      };

      return (
        <svg
          width={width}
          height={Math.max(height, controlsY + 280)}
          viewBox={`0 0 ${width} ${Math.max(height, controlsY + 280)}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: "visible" }}
        >
          <text
            x={width / 2}
            y={40}
            textAnchor="middle"
            fill="#8b5cf6"
            fontSize="16"
            fontWeight="600"
          >
            Backpropagation: How Neural Networks Learn
          </text>

          {/* Layer labels */}
          <text
            x={inputX}
            y={inYs[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Input
          </text>
          <text
            x={hidden1X}
            y={h1Ys[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Hidden 1
          </text>
          <text
            x={hidden2X}
            y={h2Ys[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Hidden 2
          </text>
          <text
            x={outputX}
            y={outYs[0] - 30}
            textAnchor="middle"
            fontSize="11"
            fill="#64748b"
            fontWeight="500"
          >
            Output
          </text>

          {/* CONNECTIONS - render differently based on update state */}
          {renderConnectionsWithUpdate(
            inputX,
            inYs,
            hidden1X,
            h1Ys,
            updatingW1
          )}
          {renderConnectionsWithUpdate(
            hidden1X,
            h1Ys,
            hidden2X,
            h2Ys,
            updatingW2
          )}
          {renderConnectionsWithUpdate(
            hidden2X,
            h2Ys,
            outputX,
            outYs,
            updatingW3
          )}

          {/* Weight layer labels - show when being updated */}
          {updatingW1 && (
            <text
              x={(inputX + hidden1X) / 2}
              y={inYs[1] - 15}
              textAnchor="middle"
              fontSize="11"
              fontWeight="700"
              fill="#f97316"
            >
              W₁ updating ⚡
            </text>
          )}
          {updatingW2 && (
            <text
              x={(hidden1X + hidden2X) / 2}
              y={h1Ys[1] - 15}
              textAnchor="middle"
              fontSize="11"
              fontWeight="700"
              fill="#f97316"
            >
              W₂ updating ⚡
            </text>
          )}
          {updatingW3 && (
            <text
              x={(hidden2X + outputX) / 2}
              y={h2Ys[1] - 15}
              textAnchor="middle"
              fontSize="11"
              fontWeight="700"
              fill="#f97316"
            >
              W₃ updating ⚡
            </text>
          )}

          {/* NEURONS WITH WEIGHTS INSIDE */}
          {inYs.map((y, i) =>
            renderNeuronWithWeight(inputX, y, 0, i, p >= FWD_1_START)
          )}
          {h1Ys.map((y, i) =>
            renderNeuronWithWeight(hidden1X, y, 1, i, p >= FWD_1_END)
          )}
          {h2Ys.map((y, i) =>
            renderNeuronWithWeight(hidden2X, y, 2, i, p >= FWD_2_END)
          )}
          {outYs.map((y, i) =>
            renderNeuronWithWeight(outputX, y, 3, i, p >= FWD_3_END)
          )}

          {/* FORWARD PASS - Blue dots traveling forward FROM ALL NEURONS */}
          {showInputToH1 &&
            renderTravelingDots(
              inYs,
              h1Ys,
              inputX,
              hidden1X,
              getSubProgress(FWD_1_START, FWD_1_END),
              "#3b82f6"
            )}
          {showH1ToH2 &&
            renderTravelingDots(
              h1Ys,
              h2Ys,
              hidden1X,
              hidden2X,
              getSubProgress(FWD_2_START, FWD_2_END),
              "#3b82f6"
            )}
          {showH2ToOut &&
            renderTravelingDots(
              h2Ys,
              outYs,
              hidden2X,
              outputX,
              getSubProgress(FWD_3_START, FWD_3_END),
              "#3b82f6"
            )}

          {/* ERROR INDICATOR */}
          {showError && (
            <g
              opacity={
                p < BWD_1_START ? 1 : Math.max(0, 1 - (p - BWD_1_START) / 0.1)
              }
            >
              <circle
                cx={outputX + 45}
                cy={outYs[1]}
                r={18}
                fill="none"
                stroke="#ef4444"
                strokeWidth="3"
              />
              <text
                x={outputX + 45}
                y={outYs[1] + 6}
                textAnchor="middle"
                fontSize="18"
                fontWeight="bold"
                fill="#ef4444"
              >
                ✗
              </text>
              <text
                x={outputX + 45}
                y={outYs[1] - 28}
                textAnchor="middle"
                fontSize="11"
                fontWeight="600"
                fill="#ef4444"
              >
                Error/Loss
              </text>
            </g>
          )}

          {/* BACKWARD PASS - Orange dots traveling backward FROM ALL NEURONS */}
          {showOutToH2 &&
            renderTravelingDots(
              outYs,
              h2Ys,
              outputX,
              hidden2X,
              getSubProgress(BWD_1_START, BWD_1_END),
              "#f97316"
            )}
          {showH2ToH1 &&
            renderTravelingDots(
              h2Ys,
              h1Ys,
              hidden2X,
              hidden1X,
              getSubProgress(BWD_2_START, BWD_2_END),
              "#f97316"
            )}
          {showH1ToIn &&
            renderTravelingDots(
              h1Ys,
              inYs,
              hidden1X,
              inputX,
              getSubProgress(BWD_3_START, BWD_3_END),
              "#f97316"
            )}

          {/* INFO PANEL */}
          <foreignObject x={30} y={controlsY} width={width - 60} height={280}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                padding: "20px",
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              {/* Current phase indicator */}
              <div
                style={{
                  background:
                    p < ERR_START
                      ? "linear-gradient(135deg, #3b82f615, #3b82f608)"
                      : p < BWD_1_START
                      ? "linear-gradient(135deg, #ef444415, #ef444408)"
                      : "linear-gradient(135deg, #f9731615, #f9731608)",
                  border:
                    p < ERR_START
                      ? "2px solid #3b82f640"
                      : p < BWD_1_START
                      ? "2px solid #ef444440"
                      : "2px solid #f9731640",
                  borderRadius: "10px",
                  padding: "14px 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div style={{ fontSize: "24px" }}>
                  {p < ERR_START ? "→" : p < BWD_1_START ? "📊" : "←"}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      color:
                        p < ERR_START
                          ? "#3b82f6"
                          : p < BWD_1_START
                          ? "#ef4444"
                          : "#f97316",
                      marginBottom: "4px",
                    }}
                  >
                    {p < ERR_START
                      ? "1. Forward Pass"
                      : p < BWD_1_START
                      ? "2. Compute Error/Loss"
                      : "3. Backward Pass (Backpropagation)"}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      lineHeight: "1.5",
                    }}
                  >
                    {p < ERR_START
                      ? "Data flows forward through fixed weights to make prediction"
                      : p < BWD_1_START
                      ? "Calculate how wrong the prediction is (loss function)"
                      : "Gradients flow backward, adjusting weights to reduce error"}
                  </div>
                </div>
              </div>

              {/* Key concept boxes */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    padding: "12px",
                    background: "#eff6ff",
                    border: "2px solid #bfdbfe",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "700",
                      color: "#3b82f6",
                      marginBottom: "6px",
                    }}
                  >
                    🔵 Forward Pass
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#475569",
                      lineHeight: "1.4",
                    }}
                  >
                    Weights stay <strong>constant</strong>. Network makes
                    prediction using current weights (shown inside neurons).
                  </div>
                </div>

                <div
                  style={{
                    padding: "12px",
                    background: "#fff7ed",
                    border: "2px solid #fed7aa",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "700",
                      color: "#f97316",
                      marginBottom: "6px",
                    }}
                  >
                    🔶 Backward Pass
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#475569",
                      lineHeight: "1.4",
                    }}
                  >
                    Weights are <strong>updated</strong>. Network learns by
                    adjusting weights (neurons pulse orange) to minimize error.
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              <div
                style={{
                  background: "#f1f5f9",
                  borderRadius: "8px",
                  padding: "10px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "4px",
                    marginBottom: "6px",
                  }}
                >
                  <div
                    style={{
                      flex: 0.45,
                      height: "8px",
                      borderRadius: "4px",
                      background: p >= FWD_3_END ? "#3b82f6" : "#cbd5e1",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {p < FWD_3_END && (
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          height: "100%",
                          width: `${(p / FWD_3_END) * 100}%`,
                          background: "#3b82f6",
                        }}
                      />
                    )}
                  </div>
                  <div
                    style={{
                      flex: 0.1,
                      height: "8px",
                      borderRadius: "4px",
                      background:
                        p >= ERR_END
                          ? "#ef4444"
                          : p >= ERR_START
                          ? "#ef4444"
                          : "#cbd5e1",
                    }}
                  />
                  <div
                    style={{
                      flex: 0.45,
                      height: "8px",
                      borderRadius: "4px",
                      background: p >= BWD_3_END ? "#f97316" : "#cbd5e1",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {p >= BWD_1_START && p < BWD_3_END && (
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          height: "100%",
                          width: `${
                            ((p - BWD_1_START) / (BWD_3_END - BWD_1_START)) *
                            100
                          }%`,
                          background: "#f97316",
                        }}
                      />
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "10px",
                    color: "#64748b",
                    fontWeight: "500",
                  }}
                >
                  <span>Forward</span>
                  <span>Error</span>
                  <span>Backward</span>
                </div>
              </div>
            </div>
          </foreignObject>
        </svg>
      );
    }

    if (concept.id === "deep") {
      const layersConfig = deepMode ? [3, 5, 7, 9, 7, 5, 3] : [3, 5, 5, 3];
      const layerX = Array.from(
        { length: layersConfig.length },
        (_, i) =>
          100 + (i * (dimensions.width - 200)) / (layersConfig.length - 1)
      );
      const maxCount = Math.max(...layersConfig);
      const layerYsAll = layersConfig.map((count) => layerYs(count, maxCount));

      return (
        <svg
          width={dimensions.width}
          height={Math.max(
            dimensions.height,
            BASE_Y + (maxCount - 1) * LAYER_GAP + 200
          )}
          viewBox={`0 0 ${dimensions.width} ${Math.max(
            dimensions.height,
            BASE_Y + (maxCount - 1) * LAYER_GAP + 200
          )}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: "visible" }}
        >
          {/* Toggle control */}
          <foreignObject
            x={dimensions.width / 2 - 120}
            y={5}
            width={240}
            height={60}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                padding: "12px 16px",
                background: "white",
                border: "2px solid #e2e8f0",
                borderRadius: "10px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: !deepMode ? "#8b5cf6" : "#94a3b8",
                }}
              >
                Shallow
              </span>
              <label
                style={{
                  position: "relative",
                  display: "inline-block",
                  width: "50px",
                  height: "26px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={deepMode}
                  onChange={() => setDeepMode(!deepMode)}
                  style={{
                    opacity: 0,
                    width: 0,
                    height: 0,
                  }}
                />
                <span
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: deepMode ? "#8b5cf6" : "#cbd5e1",
                    borderRadius: "26px",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      content: "",
                      height: "20px",
                      width: "20px",
                      left: deepMode ? "27px" : "3px",
                      bottom: "3px",
                      background: "white",
                      borderRadius: "50%",
                      transition: "all 0.3s ease",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  />
                </span>
              </label>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: deepMode ? "#8b5cf6" : "#94a3b8",
                }}
              >
                Deep
              </span>
            </div>
          </foreignObject>

          {/* Network title */}
          {/* <text
        x={dimensions.width / 2}
        y={135}
        textAnchor="middle"
        fill="#64748b"
        fontSize="13"
        fontWeight="600"
      >
        {deepMode
          ? `Deep Network: ${layersConfig.length - 2} Hidden Layers`
          : `Shallow Network: ${layersConfig.length - 2} Hidden Layers`}
      </text> */}

          {/* Layer labels */}
          {layerX.map((x, idx) => (
            <text
              key={`label-${idx}`}
              x={x}
              y={BASE_Y - 20}
              textAnchor="middle"
              fontSize="10"
              fill="#64748b"
              fontWeight="500"
            >
              {idx === 0
                ? "Input"
                : idx === layerX.length - 1
                ? "Output"
                : `H${idx}`}
            </text>
          ))}

          {/* Connections between layers */}
          {layerYsAll.slice(0, -1).map((fromYs, layerIdx) => {
            const toYs = layerYsAll[layerIdx + 1];
            return fromYs.map((fromY, fromIdx) =>
              toYs.map((toY, toIdx) => (
                <line
                  key={`conn-${layerIdx}-${fromIdx}-${toIdx}`}
                  x1={layerX[layerIdx] + 12}
                  y1={fromY}
                  x2={layerX[layerIdx + 1] - 12}
                  y2={toY}
                  stroke="#8b8285ff"
                  strokeWidth="1"
                  opacity="0.4"
                />
              ))
            );
          })}

          {/* Neurons */}
          {layerYsAll.map((ys, layerIdx) => (
            <g key={`layer-${layerIdx}`}>
              {ys.map((y, neuronIdx) => (
                <circle
                  key={`n-${layerIdx}-${neuronIdx}`}
                  cx={layerX[layerIdx]}
                  cy={y}
                  r={deepMode ? 10 : 12}
                  fill="white"
                  stroke="#8b5cf6"
                  strokeWidth="2"
                />
              ))}
            </g>
          ))}

          {/* Information panel */}
          <foreignObject
            x={40}
            y={BASE_Y + (maxCount - 1) * LAYER_GAP + 50}
            width={dimensions.width - 80}
            height={150}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                padding: "20px",
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              {deepMode ? (
                <div
                  style={{
                    background: "linear-gradient(135deg, #8b5cf615, #8b5cf608)",
                    border: "2px solid #8b5cf640",
                    borderRadius: "10px",
                    padding: "16px 20px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      color: "#8b5cf6",
                      marginBottom: "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span style={{ fontSize: "20px" }}>🏗️</span>
                    Deep Network Architecture
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#475569",
                      lineHeight: "1.6",
                    }}
                  >
                    <strong>Advantages:</strong> Can learn complex hierarchical
                    features and patterns. Each layer extracts increasingly
                    abstract representations. Better for complex tasks like
                    image recognition, natural language processing, and deep
                    pattern analysis.
                  </div>
                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "11px",
                      color: "#64748b",
                      fontStyle: "italic",
                    }}
                  >
                    Trade-off: Requires more training data and computational
                    resources
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    background: "linear-gradient(135deg, #10b98115, #10b98108)",
                    border: "2px solid #10b98140",
                    borderRadius: "10px",
                    padding: "16px 20px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      color: "#10b981",
                      marginBottom: "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span style={{ fontSize: "20px" }}>⚡</span>
                    Shallow Network Architecture
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#475569",
                      lineHeight: "1.6",
                    }}
                  >
                    <strong>Advantages:</strong> Faster to train, requires less
                    data, and easier to interpret. Processes information in
                    fewer steps. Good for simpler tasks with clear patterns like
                    basic classification and linear relationships.
                  </div>
                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "11px",
                      color: "#64748b",
                      fontStyle: "italic",
                    }}
                  >
                    Trade-off: Limited capacity for complex pattern recognition
                  </div>
                </div>
              )}

              {/* Comparison metrics */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    padding: "10px",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#64748b",
                      marginBottom: "4px",
                    }}
                  >
                    Total Layers
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#8b5cf6",
                    }}
                  >
                    {layersConfig.length}
                  </div>
                </div>
                <div
                  style={{
                    padding: "10px",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#64748b",
                      marginBottom: "4px",
                    }}
                  >
                    Neural Layers
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#8b5cf6",
                    }}
                  >
                    {layersConfig.length - 2}
                  </div>
                </div>
                <div
                  style={{
                    padding: "10px",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#64748b",
                      marginBottom: "4px",
                    }}
                  >
                    Complexity
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#8b5cf6",
                    }}
                  >
                    {deepMode ? "High" : "Low"}
                  </div>
                </div>
              </div>
            </div>
          </foreignObject>
        </svg>
      );
    }

    // FINETUNE
    if (concept.id === "finetune") {
      const categories = [
        {
          id: "medical",
          label: "Medical",
          icon: "🏥",
          predictions: [
            "Pneumonia detected (85%)",
            "Normal scan (92%)",
            "Tumor identified (78%)",
          ],
          color: "#ef4444",
        },
        {
          id: "manufacturing",
          label: "Manufacturing",
          icon: "🏭",
          predictions: [
            "Defect detected (91%)",
            "Quality passed (95%)",
            "Assembly error (82%)",
          ],
          color: "#f97316",
        },
        {
          id: "finance",
          label: "Finance",
          icon: "💰",
          predictions: [
            "Fraud detected (89%)",
            "Normal transaction (96%)",
            "Risk identified (73%)",
          ],
          color: "#10b981",
        },
        {
          id: "retail",
          label: "Retail",
          icon: "🛍️",
          predictions: [
            "Customer will buy (88%)",
            "Abandoned cart (79%)",
            "High-value customer (91%)",
          ],
          color: "#8b5cf6",
        },
      ];

      const currentCategory =
        categories.find((c) => c.id === selectedCategory) || categories[0];

      // Use animationStep to show generating animation
      const isGenerating = animationStep % 1 !== 0;
      const predictionIndex =
        Math.floor(animationStep) % currentCategory.predictions.length;
      const prediction = currentCategory.predictions[predictionIndex];

      const layerCounts = [3, 3, 3, 3];
      const maxCount = Math.max(...layerCounts);
      const inYs = layerYs(layerCounts[0], maxCount);
      const h1Ys = layerYs(layerCounts[1], maxCount);
      const h2Ys = layerYs(layerCounts[2], maxCount);
      const outYs = layerYs(layerCounts[3], maxCount);

      // Adjusted positions for better spacing
      const networkStartY = 250;
      const adjustedInYs = inYs.map((y) => y + networkStartY);
      const adjustedH1Ys = h1Ys.map((y) => y + networkStartY);
      const adjustedH2Ys = h2Ys.map((y) => y + networkStartY);
      const adjustedOutYs = outYs.map((y) => y + networkStartY);

      const controlsY = Math.max(...adjustedOutYs) + 100;

      return (
        <svg
          width={width}
          height={controlsY + 360}
          viewBox={`0 0 ${width} ${controlsY + 360}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: "visible" }}
        >
          <text
            x={width / 2}
            y={40}
            textAnchor="middle"
            fill="#8b5cf6"
            fontSize="16"
            fontWeight="600"
          >
            Transfer Learning: Pre-Training → Fine-Tuning
          </text>

          {/* Pre-training phase indicator */}
          <foreignObject x={width / 2 - 140} y={70} width={280} height={75}>
            <div
              style={{
                background: "linear-gradient(135deg, #3b82f615, #3b82f608)",
                border: "2px solid #3b82f640",
                borderRadius: "10px",
                padding: "10px 14px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "700",
                  color: "#3b82f6",
                  marginBottom: "4px",
                }}
              >
                🌐 Step 1: Pre-Training
              </div>
              <div
                style={{
                  fontSize: "10px",
                  color: "#64748b",
                  lineHeight: "1.3",
                }}
              >
                General knowledge from large datasets
              </div>
            </div>
          </foreignObject>

          {/* Arrow from pre-training to network */}
          <path
            d={`M ${width / 2} 150 L ${width / 2} 190`}
            stroke="#94a3b8"
            strokeWidth="2"
            fill="none"
            markerEnd="url(#arrowhead)"
          />

          {/* Step 2 indicator above network */}
          <foreignObject x={width / 2 - 140} y={195} width={280} height={50}>
            <div
              style={{
                background: "linear-gradient(135deg, #8b5cf615, #8b5cf608)",
                border: "2px solid #8b5cf640",
                borderRadius: "8px",
                padding: "8px 12px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "700",
                  color: "#8b5cf6",
                  marginBottom: "2px",
                }}
              >
                🎯 Step 2: Fine-Tuning
              </div>
            </div>
          </foreignObject>

          {/* Layer labels */}
          <text
            x={inputX}
            y={adjustedInYs[0] - 20}
            textAnchor="middle"
            fontSize="10"
            fill="#64748b"
            fontWeight="500"
          >
            Input
          </text>
          <text
            x={hidden1X}
            y={adjustedH1Ys[0] - 20}
            textAnchor="middle"
            fontSize="10"
            fill="#64748b"
            fontWeight="500"
          >
            Hidden 1
          </text>
          <text
            x={hidden2X}
            y={adjustedH2Ys[0] - 20}
            textAnchor="middle"
            fontSize="10"
            fill="#64748b"
            fontWeight="500"
          >
            Hidden 2
          </text>
          <text
            x={outputX}
            y={adjustedOutYs[0] - 20}
            textAnchor="middle"
            fontSize="10"
            fill="#64748b"
            fontWeight="500"
          >
            Output
          </text>

          {/* Frozen layers box */}
          <rect
            x={inputX - 40}
            y={Math.min(...adjustedInYs) - 35}
            width={hidden1X - inputX + 80}
            height={Math.max(...adjustedInYs) - Math.min(...adjustedInYs) + 70}
            fill="#e0e7ff15"
            stroke="#a5b4fc"
            strokeWidth="2"
            strokeDasharray="5,5"
            rx="8"
          />
          <text
            x={(inputX + hidden1X) / 2}
            y={Math.max(...adjustedInYs) + 50}
            textAnchor="middle"
            fontSize="10"
            fill="#6366f1"
            fontWeight="600"
          >
            🔒 Frozen Layers
          </text>

          {/* Fine-tuned layers box */}
          <rect
            x={hidden2X - 40}
            y={Math.min(...adjustedH2Ys) - 35}
            width={outputX - hidden2X + 80}
            height={Math.max(...adjustedH2Ys) - Math.min(...adjustedH2Ys) + 70}
            fill={`${currentCategory.color}15`}
            stroke={currentCategory.color}
            strokeWidth="2"
            rx="8"
          />
          <text
            x={(hidden2X + outputX) / 2}
            y={Math.max(...adjustedOutYs) + 50}
            textAnchor="middle"
            fontSize="10"
            fill={currentCategory.color}
            fontWeight="600"
          >
            🔥 Fine-Tuned
          </text>

          {/* Neurons with pulsing animation when generating */}
          {adjustedInYs.map((y, i) => (
            <g key={`in-${i}`}>
              <circle
                cx={inputX}
                cy={y}
                r="12"
                fill="white"
                stroke="#94a3b8"
                strokeWidth="2"
              />
              {isGenerating && (
                <circle
                  cx={inputX}
                  cy={y}
                  r="12"
                  fill="none"
                  stroke="#a03ba1ff"
                  strokeWidth="2"
                  opacity="0.6"
                >
                  <animate
                    attributeName="r"
                    values="12;16;12"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.6;0;0.6"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}
            </g>
          ))}
          {adjustedH1Ys.map((y, i) => (
            <g key={`h1-${i}`}>
              <circle
                cx={hidden1X}
                cy={y}
                r="12"
                fill="white"
                stroke="#94a3b8"
                strokeWidth="2"
              />
              {isGenerating && (
                <circle
                  cx={hidden1X}
                  cy={y}
                  r="12"
                  fill="none"
                  stroke="#a03ba1ff"
                  strokeWidth="2"
                  opacity="0.6"
                >
                  <animate
                    attributeName="r"
                    values="12;16;12"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.6;0;0.6"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}
            </g>
          ))}
          {adjustedH2Ys.map((y, i) => (
            <g key={`h2-${i}`}>
              <circle
                cx={hidden2X}
                cy={y}
                r="12"
                fill="white"
                stroke={isGenerating ? currentCategory.color : "#94a3b8"}
                strokeWidth="2"
              />
              {isGenerating && (
                <circle
                  cx={hidden2X}
                  cy={y}
                  r="12"
                  fill="none"
                  stroke={currentCategory.color}
                  strokeWidth="3"
                  opacity="0.8"
                >
                  <animate
                    attributeName="r"
                    values="12;18;12"
                    dur="0.8s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.8;0;0.8"
                    dur="0.8s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}
            </g>
          ))}
          {adjustedOutYs.map((y, i) => (
            <g key={`out-${i}`}>
              <circle
                cx={outputX}
                cy={y}
                r="12"
                fill="white"
                stroke={isGenerating ? currentCategory.color : "#94a3b8"}
                strokeWidth="2"
              />
              {isGenerating && (
                <circle
                  cx={outputX}
                  cy={y}
                  r="12"
                  fill="none"
                  stroke={currentCategory.color}
                  strokeWidth="3"
                  opacity="0.8"
                >
                  <animate
                    attributeName="r"
                    values="12;18;12"
                    dur="0.8s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.8;0;0.8"
                    dur="0.8s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}
            </g>
          ))}

          {/* Connections */}
          {renderConnectionsBetween(
            inputX,
            adjustedInYs,
            hidden1X,
            adjustedH1Ys
          )}
          {renderConnectionsBetween(
            hidden1X,
            adjustedH1Ys,
            hidden2X,
            adjustedH2Ys
          )}
          {renderConnectionsBetween(
            hidden2X,
            adjustedH2Ys,
            outputX,
            adjustedOutYs
          )}

          {/* Fine-tuning controls */}
          <foreignObject x={30} y={controlsY} width={width - 60} height={360}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                padding: "20px",
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              {/* Domain selection */}
              <div
                style={{
                  background: "white",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "16px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                }}
              >
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#475569",
                    marginBottom: "12px",
                  }}
                >
                  Select a fine-tuned domain:
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: "10px",
                  }}
                >
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      style={{
                        padding: "12px 8px",
                        fontSize: "12px",
                        fontWeight: "600",
                        color:
                          selectedCategory === category.id
                            ? "white"
                            : "#64748b",
                        background:
                          selectedCategory === category.id
                            ? category.color
                            : "#f8fafc",
                        border: "2px solid",
                        borderColor:
                          selectedCategory === category.id
                            ? category.color
                            : "#e2e8f0",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "4px",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedCategory !== category.id) {
                          e.currentTarget.style.borderColor = category.color;
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedCategory !== category.id) {
                          e.currentTarget.style.borderColor = "#e2e8f0";
                          e.currentTarget.style.transform = "translateY(0)";
                        }
                      }}
                    >
                      <span style={{ fontSize: "22px" }}>{category.icon}</span>
                      <span>{category.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Prediction result */}
              <div
                style={{
                  background: `linear-gradient(135deg, ${currentCategory.color}20, ${currentCategory.color}10)`,
                  border: `2px solid ${currentCategory.color}`,
                  borderRadius: "10px",
                  padding: "18px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#64748b",
                    marginBottom: "10px",
                  }}
                >
                  {currentCategory.icon} Fine-Tuned Prediction for{" "}
                  {currentCategory.label}:
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: currentCategory.color,
                    padding: "14px 16px",
                    background: "white",
                    borderRadius: "8px",
                    marginBottom: "14px",
                    minHeight: "50px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `1px solid ${currentCategory.color}30`,
                    opacity: isGenerating ? 0.6 : 1,
                    transition: "opacity 0.3s ease",
                  }}
                >
                  {isGenerating
                    ? "🔄 Generating prediction..."
                    : `✓ ${prediction}`}
                </div>

                <button
                  onClick={() => {
                    const start = animationStep;
                    const duration = 1000;
                    const startTime = Date.now();

                    const animate = () => {
                      const elapsed = Date.now() - startTime;
                      const progress = Math.min(elapsed / duration, 1);
                      setAnimationStep(start + progress);

                      if (progress < 1) {
                        requestAnimationFrame(animate);
                      } else {
                        setAnimationStep(Math.floor(start) + 1);
                      }
                    };

                    animate();
                  }}
                  disabled={isGenerating}
                  style={{
                    width: "100%",
                    padding: "12px 20px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "white",
                    background: isGenerating
                      ? "#cbd5e1"
                      : `linear-gradient(135deg, ${currentCategory.color}, ${currentCategory.color}dd)`,
                    border: "none",
                    borderRadius: "8px",
                    cursor: isGenerating ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                    boxShadow: isGenerating
                      ? "none"
                      : `0 3px 6px ${currentCategory.color}40`,
                  }}
                  onMouseEnter={(e) => {
                    if (!isGenerating) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = `0 5px 10px ${currentCategory.color}50`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isGenerating) {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = `0 3px 6px ${currentCategory.color}40`;
                    }
                  }}
                >
                  {isGenerating
                    ? "⏳ Processing..."
                    : "🔄 Generate New Prediction"}
                </button>
              </div>

              {/* Info box */}
              <div
                style={{
                  padding: "12px 16px",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "11px",
                  color: "#64748b",
                  lineHeight: "1.5",
                }}
              >
                <strong style={{ color: "#475569" }}>
                  💡 Transfer Learning:
                </strong>{" "}
                Pre-trained models learn general features (frozen layers), then
                fine-tune specific layers for your domain with less data and
                faster training.
              </div>
            </div>
          </foreignObject>

          {/* Arrow marker definition */}
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="10"
              refX="5"
              refY="3"
              orient="auto"
            >
              <polygon points="0 0, 10 3, 0 6" fill="#94a3b8" />
            </marker>
          </defs>
        </svg>
      );
    }

    return null;
  };

  return (
    <div className="nne-container" ref={containerRef}>
      <div className="nne-wrapper">
        <div>
          <header
            style={{
              textAlign: "center",
              marginBottom: "3rem",
              animation: "fadeIn 0.8s ease-out",
            }}
          >
            <h1
              style={{
                fontSize: "3rem",
                fontWeight: "800",
                color: "#1f2937",
                marginBottom: "1rem",
                letterSpacing: "-0.02em",
              }}
            >
              Neural Network
            </h1>
            <p
              style={{
                fontSize: "1.2rem",
                color: "#4b5563",
                maxWidth: "700px",
                margin: "0 auto",
                lineHeight: "1.7",
              }}
            >
              The basics of all intelligent systems
            </p>
          </header>
        </div>

        <div className="nne-nav">
          {concepts.map((c, idx) => {
            const Icon = c.icon;
            return (
              <button
                key={c.id}
                onClick={() => setCurrentConcept(idx)}
                className={`nne-nav-item ${
                  idx === currentConcept ? "active" : ""
                }`}
              >
                <Icon size={18} />
                <span>{c.title.split(" -")[0]}</span>
              </button>
            );
          })}
        </div>

        <div className="nne-card">
          <div className="nne-card-header">
            <h2>{concept.title}</h2>
            <p>{concept.description}</p>
          </div>
          <div className="nne-viz-box">{renderVisualization()}</div>
        </div>

        <div className="nne-footer">
          <p>
            <strong>💡 Pro Tip:</strong> Each concept builds on the previous
            one. Start with "Neuron" and work your way through!
          </p>
        </div>
      </div>
    </div>
  );
};

export default NeuralNetworkExplorer;
